/**
 * FITUEYES Editorial Landing — Bundled Client Controller
 * Self-contained production build without ES module imports.
 */
(function() {
  'use strict';

  if (window.__EDITORIAL_LANDING_JS_LOADED__) {
    return;
  }
  window.__EDITORIAL_LANDING_JS_LOADED__ = true;

/* === Module: navigation.js === */
/**
 * Navigation Module
 * Handles sticky navbar styling, scrollspy link activation, mobile drawer controls,
 * window resize safety, and header search toggle.
 */
let activeMenuTrigger = null;

function closeDrawer() {
  const drawer = document.getElementById('mobileDrawer');
  const menuToggle = document.querySelector('.mobile-menu-toggle');
  if (!drawer || !drawer.classList.contains('is-open')) return;
  drawer.classList.remove('is-open');
  menuToggle?.classList.remove('is-open');
  menuToggle?.setAttribute('aria-expanded', 'false');
  drawer.setAttribute('aria-hidden', 'true');
  document.body.classList.remove('no-scroll');
  document.documentElement.classList.remove('has-modal');
  if (activeMenuTrigger && typeof activeMenuTrigger.focus === 'function') {
    activeMenuTrigger.focus();
  }
}

function initNavigation() {
  const header = document.querySelector('.editorial-header');
  if (!header || header.dataset.navInit === 'true') return;
  header.dataset.navInit = 'true';
  const menuToggle = document.querySelector('.mobile-menu-toggle');
  const drawer = document.querySelector('.mobile-drawer');
  const drawerBackdrop = document.querySelector('.drawer-backdrop');
  const drawerCloseBtn = document.querySelector('.drawer-close-btn');
  const navLinks = document.querySelectorAll('.header-nav .nav-link');
  const allNavAnchorLinks = document.querySelectorAll('.nav-link, .drawer-link');

  // Search Bar Elements
  const searchToggleBtn = document.querySelector('[data-toggle-search]');
  const searchBar = document.getElementById('headerSearchBar');
  const searchCloseBtn = searchBar?.querySelector('.search-close-btn');
  const searchInput = searchBar?.querySelector('.search-input');

  // 1. Sticky Header scroll listener
  if (header) {
    let isHeaderScrolled = null;
    const handleScroll = () => {
      const shouldBeScrolled = window.scrollY > 40;
      if (isHeaderScrolled !== shouldBeScrolled) {
        isHeaderScrolled = shouldBeScrolled;
        header.classList.toggle('is-scrolled', shouldBeScrolled);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
  }

  // 2. Mobile Drawer Controls
  function openDrawerInternal() {
    if (!drawer) return;
    activeMenuTrigger = menuToggle;

    // Close cart drawer if open to prevent stacking backdrop traps
    const cartDrawer = document.getElementById('cartDrawer');
    if (cartDrawer?.classList.contains('is-open')) {
      cartDrawer.classList.remove('is-open');
      cartDrawer.setAttribute('aria-hidden', 'true');
    }

    drawer.classList.add('is-open');
    menuToggle?.classList.add('is-open');
    menuToggle?.setAttribute('aria-expanded', 'true');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('no-scroll');
    document.documentElement.classList.add('has-modal');
    setTimeout(() => drawerCloseBtn?.focus(), 50);
  }

  function closeDrawerInternal() {
    closeDrawer();
  }

  menuToggle?.addEventListener('click', () => {
    const isOpen = drawer?.classList.contains('is-open');
    if (isOpen) closeDrawerInternal();
    else openDrawerInternal();
  });

  drawerBackdrop?.addEventListener('click', closeDrawerInternal);
  drawerCloseBtn?.addEventListener('click', closeDrawerInternal);

  // Close drawer on ESC key
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (drawer?.classList.contains('is-open')) closeDrawerInternal();
      if (searchBar && !searchBar.hidden) closeSearch();
    }
  });

  // Edge case: Close drawer if window is resized to desktop width (>= 992px)
  window.addEventListener('resize', () => {
    if (window.innerWidth >= 992 && drawer?.classList.contains('is-open')) {
      closeDrawerInternal();
    }
  });

  // 3. Search Bar Controls
  function toggleSearch() {
    if (!searchBar) return;
    const isHidden = searchBar.hidden;
    if (isHidden) {
      searchBar.hidden = false;
      searchToggleBtn?.setAttribute('aria-expanded', 'true');
      setTimeout(() => searchInput?.focus(), 50);
    } else {
      closeSearch();
    }
  }

  function closeSearch() {
    if (!searchBar) return;
    searchBar.hidden = true;
    searchToggleBtn?.setAttribute('aria-expanded', 'false');
    searchToggleBtn?.focus();
  }

  searchToggleBtn?.addEventListener('click', toggleSearch);
  searchCloseBtn?.addEventListener('click', closeSearch);

  // Search input filter feedback & section routing
  searchBar?.querySelector('.search-form')?.addEventListener('submit', (e) => {
    e.preventDefault();
    const query = (searchInput?.value || '').trim();
    if (query) {
      window.dispatchEvent(new CustomEvent('editorial:search', { detail: { query } }));
      closeSearch();

      const q = query.toLowerCase();
      let targetSection = null;
      if (q.includes('as07') || q.includes('compact') || q.includes('tactile')) {
        targetSection = document.getElementById('as07');
      } else if (q.includes('rs10') || q.includes('cement') || q.includes('arrangement') || q.includes('expanded')) {
        targetSection = document.getElementById('rs10');
      } else if (q.includes('showcase') || q.includes('gallery') || q.includes('perspective')) {
        targetSection = document.getElementById('gallery');
      } else if (q.includes('dual') || q.includes('enclosed') || q.includes('rs05') || q.includes('silhouette')) {
        targetSection = document.querySelector('.section-dual-showcase');
      } else if (q.includes('picasso') || q.includes('walnut') || q.includes('oak') || q.includes('white') || q.includes('edit') || q.includes('catalog')) {
        targetSection = document.getElementById('listening-edit');
        if (q.includes('picasso')) {
          document.querySelector(".filter-btn[data-filter='picasso']")?.click();
        } else if (q.includes('eiffel')) {
          document.querySelector(".filter-btn[data-filter='eiffel']")?.click();
        } else if (q.includes('compact')) {
          document.querySelector(".filter-btn[data-filter='compact']")?.click();
        }
      } else if (q.includes('spec') || q.includes('dim') || q.includes('timber') || q.includes('acoustic') || q.includes('storage') || q.includes('craft')) {
        targetSection = document.getElementById('specs');
      } else if (q.includes('faq') || q.includes('ship') || q.includes('clean') || q.includes('warranty') || q.includes('turntable')) {
        targetSection = document.getElementById('faq');
      }

      if (targetSection) {
        const headerOffset = header ? header.offsetHeight : 0;
        const elementPosition = targetSection.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  });

  // 4. Smooth Anchor Scroll & Close Mobile Menu
  allNavAnchorLinks.forEach((link) => {
    link.addEventListener('click', (e) => {
      const targetId = link.getAttribute('href');
      if (targetId && targetId.startsWith('#') && targetId.length > 1) {
        const targetEl = document.querySelector(targetId);
        if (targetEl) {
          e.preventDefault();
          closeDrawer();
          const headerOffset = header ? header.offsetHeight : 0;
          const elementPosition = targetEl.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }
    });
  });

  // 5. Scrollspy for Active Section Navigation
  if ('IntersectionObserver' in window && navLinks.length) {
    const sections = Array.from(navLinks)
      .map((link) => {
        const id = link.getAttribute('href')?.replace('#', '');
        return id ? document.getElementById(id) : null;
      })
      .filter(Boolean);

    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0
    };

    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const currentId = `#${entry.target.id}`;
          navLinks.forEach((link) => {
            if (link.getAttribute('href') === currentId) {
              link.classList.add('is-active');
            } else {
              link.classList.remove('is-active');
            }
          });
        }
      });
    }, observerOptions);

    sections.forEach((sec) => sectionObserver.observe(sec));
  }
}

/* === Module: quickview.js === */
/**
 * Quick View Dialog Modal Module
 * Native accessible <dialog> popup with full keyboard trapping, focus restoration,
 * and accurate product detail resolution.
 */
let lastActiveElement = null;
let savedScrollY = 0;

function closeQuickViewModal() {
  const modal = document.getElementById('quickViewModal');
  if (!modal) return;
  if (typeof modal.close === 'function') {
    modal.close();
  } else {
    modal.removeAttribute('open');
  }
  document.body.classList.remove('no-scroll');
  document.documentElement.classList.remove('has-modal');
  if (typeof savedScrollY === 'number' && savedScrollY >= 0) {
    window.scrollTo({ top: savedScrollY, behavior: 'instant' });
  }
  if (lastActiveElement && typeof lastActiveElement.focus === 'function') {
    try {
      lastActiveElement.focus({ preventScroll: true });
    } catch (err) {
      lastActiveElement.focus();
    }
  }
}

let currentCarouselIndex = 0;
let carouselImages = [];

function goToSlide(index) {
  const modal = document.getElementById('quickViewModal');
  if (!modal || carouselImages.length === 0) return;
  const track = modal.querySelector('[data-carousel-track]');
  const pagination = modal.querySelector('[data-carousel-pagination]');

  if (window.getSelection) {
    const sel = window.getSelection();
    if (sel && sel.removeAllRanges) {
      sel.removeAllRanges();
    }
  }

  currentCarouselIndex = (index + carouselImages.length) % carouselImages.length;
  if (track) {
    track.style.transform = `translateX(-${currentCarouselIndex * 100}%)`;
  }

  const slides = modal.querySelectorAll('.modal-carousel-slide');
  slides.forEach((slide, idx) => {
    const isActive = idx === currentCarouselIndex;
    slide.classList.toggle('is-active', isActive);
    const img = slide.querySelector('img');
    if (img) {
      if (isActive) {
        img.classList.add('modal-product-img');
      } else {
        img.classList.remove('modal-product-img');
      }
    }
  });

  if (pagination) {
    const dots = pagination.querySelectorAll('.modal-carousel-dot');
    dots.forEach((dot, idx) => {
      const isActive = idx === currentCarouselIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }
}

function getCleanImagePath(url) {
  if (!url) return '';
  try {
    const withoutQuery = url.split('?')[0].split('#')[0];
    const parts = withoutQuery.split('/');
    let filename = parts[parts.length - 1] || '';
    filename = filename.replace(/_(?:\d+x\d*|\d*x\d+|master)\.(jpg|jpeg|png|webp|gif)$/i, '.$1');
    return decodeURIComponent(filename).toLowerCase();
  } catch (e) {
    return url.toLowerCase();
  }
}

function findImageIndexInCarousel(imgSrc, fallbackIndex) {
  if (imgSrc && carouselImages.length > 0) {
    const cleanTarget = getCleanImagePath(imgSrc);
    if (cleanTarget) {
      const found = carouselImages.findIndex((img) => {
        if (img === imgSrc) return true;
        const cleanImg = getCleanImagePath(img);
        return cleanImg && (cleanImg === cleanTarget || cleanImg.includes(cleanTarget) || cleanTarget.includes(cleanImg));
      });
      if (found !== -1) return found;
    }
  }
  if (fallbackIndex !== undefined && fallbackIndex !== null && fallbackIndex !== '') {
    const num = parseInt(fallbackIndex, 10);
    if (!isNaN(num) && num >= 0 && num < carouselImages.length) {
      return num;
    }
  }
  return -1;
}

function goToImage(imgSrc, targetIndex) {
  if (carouselImages.length === 0) return;
  if (window.getSelection) {
    const sel = window.getSelection();
    if (sel && sel.removeAllRanges) {
      sel.removeAllRanges();
    }
  }
  const idx = findImageIndexInCarousel(imgSrc, targetIndex);
  if (idx !== -1) {
    goToSlide(idx);
  } else if (imgSrc) {
    const modal = document.getElementById('quickViewModal');
    const activeImg = modal?.querySelector('.modal-carousel-slide.is-active img') || modal?.querySelector('.modal-product-img');
    if (activeImg) activeImg.src = imgSrc;
  }
}

function setCarousel(images, activeImage, productUrl, initialIndex) {
  const modal = document.getElementById('quickViewModal');
  if (!modal) return;
  const track = modal.querySelector('[data-carousel-track]');
  const prevBtn = modal.querySelector('[data-carousel-prev]');
  const nextBtn = modal.querySelector('[data-carousel-next]');
  const pagination = modal.querySelector('[data-carousel-pagination]');
  if (!track) return;

  const uniqueImages = [];
  const seenPaths = new Set();
  if (Array.isArray(images)) {
    images.forEach((img) => {
      if (!img) return;
      const clean = getCleanImagePath(img);
      if (clean && !seenPaths.has(clean)) {
        seenPaths.add(clean);
        uniqueImages.push(img);
      } else if (!clean && !uniqueImages.includes(img)) {
        uniqueImages.push(img);
      }
    });
  }
  if (uniqueImages.length === 0 && activeImage) {
    uniqueImages.push(activeImage);
  }
  if (uniqueImages.length === 0) {
    uniqueImages.push('assets/images/product-picasso-walnut.jpg');
  }

  carouselImages = uniqueImages;
  currentCarouselIndex = 0;

  const foundIdx = findImageIndexInCarousel(activeImage, initialIndex);
  if (foundIdx !== -1) {
    currentCarouselIndex = foundIdx;
  }

  const multi = carouselImages.length > 1;
  if (prevBtn) prevBtn.classList.toggle('is-hidden', !multi);
  if (nextBtn) nextBtn.classList.toggle('is-hidden', !multi);
  if (pagination) pagination.style.display = multi ? 'flex' : 'none';

  track.innerHTML = '';
  carouselImages.forEach((imgUrl, idx) => {
    const slide = document.createElement('div');
    slide.className = 'modal-carousel-slide' + (idx === currentCarouselIndex ? ' is-active' : '');
    const a = document.createElement('a');
    a.href = productUrl || '#';
    a.className = 'modal-image-link';
    a.setAttribute('aria-label', 'View product details');
    a.setAttribute('draggable', 'false');
    const img = document.createElement('img');
    img.src = imgUrl;
    img.alt = 'Selected turntable stand';
    img.className = 'modal-slide-img' + (idx === currentCarouselIndex ? ' modal-product-img' : '');
    img.setAttribute('draggable', 'false');
    a.appendChild(img);
    slide.appendChild(a);
    track.appendChild(slide);
  });

  if (pagination) {
    pagination.innerHTML = '';
    carouselImages.forEach((_, idx) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'modal-carousel-dot' + (idx === currentCarouselIndex ? ' is-active' : '');
      dot.setAttribute('aria-label', `Go to image ${idx + 1}`);
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-selected', idx === currentCarouselIndex ? 'true' : 'false');
      dot.addEventListener('mousedown', (e) => {
        e.preventDefault();
      });
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        goToSlide(idx);
      });
      pagination.appendChild(dot);
    });
  }

  track.style.transform = `translateX(-${currentCarouselIndex * 100}%)`;
}

function initQuickView(cartManager, container) {
  const modal = document.getElementById('quickViewModal');
  const root = (container && container.querySelectorAll) ? container : document;
  const quickViewTriggers = root.querySelectorAll('[data-quickview-btn]');

  if (!modal) return;
  if (modal.parentElement !== document.body) {
    document.body.appendChild(modal);
  }

  // Carousel controls initialization
  const prevBtn = modal.querySelector('[data-carousel-prev]');
  const nextBtn = modal.querySelector('[data-carousel-next]');

  if (!modal.dataset.carouselInit) {
    modal.dataset.carouselInit = 'true';
    prevBtn?.addEventListener('mousedown', (e) => {
      e.preventDefault();
    });
    prevBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      goToSlide(currentCarouselIndex - 1);
    });
    nextBtn?.addEventListener('mousedown', (e) => {
      e.preventDefault();
    });
    nextBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      goToSlide(currentCarouselIndex + 1);
    });

    window.addEventListener('keydown', (e) => {
      if (modal.open && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        if (e.key === 'ArrowLeft') goToSlide(currentCarouselIndex - 1);
        if (e.key === 'ArrowRight') goToSlide(currentCarouselIndex + 1);
      }
    });

    const track = modal.querySelector('[data-carousel-track]');
    let touchStartX = 0;
    track?.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });
    track?.addEventListener('touchend', (e) => {
      const diff = touchStartX - e.changedTouches[0].screenX;
      if (diff > 40) {
        goToSlide(currentCarouselIndex + 1);
      } else if (diff < -40) {
        goToSlide(currentCarouselIndex - 1);
      }
    }, { passive: true });
  }

  function openQuickView(productData, triggerBtn) {
    lastActiveElement = triggerBtn || document.activeElement;
    savedScrollY = window.scrollY || document.documentElement.scrollTop || 0;

    // Populate carousel
    setCarousel(productData.images || [productData.image], productData.image, productData.url, productData.activeImageIndex);

    // Populate modal elements
    const titleEl = modal.querySelector('.modal-product-title');
    const subtitleEl = modal.querySelector('.modal-product-subtitle');
    const priceEl = modal.querySelector('.modal-product-price');
    const modalInfo = modal.querySelector('.modal-info');
    const swatchesWrap = modal.querySelector('.modal-swatches-wrap');
    const modalSwatches = modal.querySelectorAll('.modal-swatches .swatch-btn');

    if (titleEl && productData.title) titleEl.textContent = productData.title;
    if (subtitleEl && productData.subtitle) subtitleEl.textContent = productData.subtitle;
    if (priceEl && productData.price) priceEl.textContent = productData.price;

    const viewLink = modal.querySelector('.modal-view-link');
    const titleLink = modal.querySelector('.modal-title-link');
    if (viewLink && productData.url) viewLink.href = productData.url;
    if (titleLink && productData.url) titleLink.href = productData.url;

    if (modalInfo) {
      modalInfo.setAttribute('data-product-id', productData.id || 'quick-item');
      modalInfo.setAttribute('data-product-name', productData.title || 'FITUEYES Stand');
      modalInfo.setAttribute('data-product-price', (productData.price || '$499').replace(/[^0-9.]/g, ''));
      modalInfo.setAttribute('data-product-variant', productData.variant || 'American Walnut');
      if (productData.variantId) modalInfo.setAttribute('data-variant-id', productData.variantId);
      if (productData.url) modalInfo.setAttribute('data-product-url', productData.url);
    }

    // Synchronize or hide swatches based on product capability
    if (productData.hasSwatches === false) {
      if (swatchesWrap) swatchesWrap.style.display = 'none';
    } else if (productData.cardSwatches && productData.cardSwatches.length > 0) {
      if (swatchesWrap) swatchesWrap.style.display = '';
      const modalSwatchesWrapEl = modal.querySelector('.modal-swatches');
      if (modalSwatchesWrapEl) {
        modalSwatchesWrapEl.innerHTML = '';
        productData.cardSwatches.forEach((cs) => {
          const ms = document.createElement('button');
          ms.type = 'button';
          ms.className = cs.className;
          const fName = cs.getAttribute('data-finish') || '';
          let sColor = '';
          const fLower = fName.toLowerCase();
          if (fLower.includes('walnut') || fLower.includes('walnuss')) sColor = '#663622';
          else if (fLower.includes('maple') || fLower.includes('ahorn')) sColor = '#DFC698';
          else if (fLower.includes('black') || fLower.includes('schwarz')) sColor = '#000000';
          else if (fLower.includes('oak') || fLower.includes('eiche')) sColor = '#efebd6';
          else if (fLower.includes('white') || fLower.includes('birch') || fLower.includes('weiss') || fLower.includes('weiß')) sColor = '#ffffff';
          else if (fLower.includes('cement') || fLower.includes('gray') || fLower.includes('grey')) sColor = '#9e9c96';
          else if (fLower.includes('natural') || fLower.includes('natur')) sColor = '#DFC698';
          else if (fLower.includes('pink') || fLower.includes('rosa')) sColor = '#FFC0CB';
          else if (cs.style.backgroundColor && cs.style.backgroundColor !== 'initial' && cs.style.backgroundColor !== 'inherit' && cs.style.backgroundColor !== 'transparent') {
            sColor = cs.style.backgroundColor;
          } else if (cs.classList.contains('swatch-walnut')) sColor = '#663622';
          else if (cs.classList.contains('swatch-maple')) sColor = '#DFC698';
          else if (cs.classList.contains('swatch-oak')) sColor = '#efebd6';
          else if (cs.classList.contains('swatch-white')) sColor = '#ffffff';
          else if (cs.classList.contains('swatch-black')) sColor = '#000000';
          else if (cs.classList.contains('swatch-cement')) sColor = '#9e9c96';
          else if (cs.classList.contains('swatch-pink')) sColor = '#FFC0CB';
          else sColor = '#333333';
          ms.setAttribute('data-finish', fName);
          ms.setAttribute('data-image', cs.getAttribute('data-image') || '');
          ms.setAttribute('data-image-index', cs.getAttribute('data-image-index') || '');
          ms.setAttribute('data-price', cs.getAttribute('data-price') || '');
          ms.setAttribute('data-price-raw', cs.getAttribute('data-price-raw') || '');
          ms.setAttribute('data-variant-id', cs.getAttribute('data-variant-id') || '');
          ms.setAttribute('data-variant-url', cs.getAttribute('data-variant-url') || '');
          ms.setAttribute('aria-label', cs.getAttribute('aria-label') || fName);
          ms.innerHTML = `<span class="swatch-chip-color" style="background-color: ${sColor};"></span><span class="swatch-chip-name">${fName}</span>`;

          ms.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            modalSwatchesWrapEl.querySelectorAll('.swatch-btn').forEach(s => s.classList.remove('is-active'));
            ms.classList.add('is-active');

            const fImg = ms.getAttribute('data-image');
            const fIndex = ms.getAttribute('data-image-index');
            const fPrice = ms.getAttribute('data-price');
            const fPriceRaw = ms.getAttribute('data-price-raw');
            const vId = ms.getAttribute('data-variant-id');
            const vUrl = ms.getAttribute('data-variant-url');

            goToImage(fImg, fIndex);
            if (subtitleEl && fName) {
              subtitleEl.textContent = `Record Player Stand · ${fName}`;
            }
            if (priceEl && fPrice) {
              priceEl.textContent = fPrice;
            }
            if (modalInfo) {
              if (fName) modalInfo.setAttribute('data-product-variant', fName);
              if (fPriceRaw) modalInfo.setAttribute('data-product-price', fPriceRaw);
              if (vId) modalInfo.setAttribute('data-variant-id', vId);
            }
            if (vUrl) {
              if (viewLink) viewLink.href = vUrl;
              if (titleLink) titleLink.href = vUrl;
            }
            // Sync with card
            cs.click();
          });
          modalSwatchesWrapEl.appendChild(ms);
        });
      }
    } else {
      if (swatchesWrap) swatchesWrap.style.display = '';
      let matched = false;
      modalSwatches.forEach((swatch) => {
        const finish = (swatch.getAttribute('data-finish') || '').toLowerCase();
        const v = (productData.variant || '').toLowerCase();
        if (
          v &&
          (finish.includes(v) ||
            v.includes(finish) ||
            (v.includes('walnut') && finish.includes('walnut')) ||
            (v.includes('maple') && finish.includes('maple')) ||
            (v.includes('black') && finish.includes('black')) ||
            (v.includes('oak') && finish.includes('oak')) ||
            (v.includes('white') && finish.includes('white')) ||
            (v.includes('cement') && finish.includes('cement')) ||
            (v.includes('pink') && finish.includes('pink')))
        ) {
          modalSwatches.forEach((s) => s.classList.remove('is-active'));
          swatch.classList.add('is-active');
          matched = true;
        }
        // Wire image sync for static modal swatches
        if (!swatch.dataset.carouselBound) {
          swatch.dataset.carouselBound = 'true';
          swatch.addEventListener('click', () => {
            const fImg = swatch.getAttribute('data-image');
            const fIndex = swatch.getAttribute('data-image-index');
            goToImage(fImg, fIndex);
          });
        }
      });
      if (!matched && modalSwatches.length) {
        modalSwatches.forEach((s, idx) => s.classList.toggle('is-active', idx === 0));
      }
    }

    if (typeof modal.showModal === 'function') {
      modal.showModal();
    } else {
      modal.setAttribute('open', '');
    }
    document.body.classList.add('no-scroll');
    document.documentElement.classList.add('has-modal');
  }

  quickViewTriggers.forEach((btn) => {
    if (btn.dataset.quickviewInit === 'true') return;
    btn.dataset.quickviewInit = 'true';

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const section = btn.closest('section');
      const productCard = btn.closest('.product-card');
      const dualCard = btn.closest('.dual-card');
      const as07Content = btn.closest('.as07-content');
      const rs10Content = btn.closest('.rs10-content');

      let title = 'Record Player Stand';
      let subtitle = 'Handcrafted Vinyl Storage Stand';
      let price = '$499.00';
      let image = 'assets/images/as07-lifestyle-main.jpg';
      let id = 'item-' + Date.now();
      let variant = 'Standard Edition';
      let hasSwatches = true;
      let url = '#';
      let variantId = '';
      let cardSwatches = null;
      let images = [];
      let activeImageIndex = null;

      if (productCard) {
        id = productCard.getAttribute('data-product-id') || id;
        title = productCard.querySelector('.product-title')?.textContent?.trim() || title;
        subtitle = productCard.querySelector('.product-subtitle')?.textContent?.trim() || subtitle;
        price = productCard.querySelector('.product-price')?.textContent?.trim() || '$' + (productCard.getAttribute('data-product-price') || '499.00');
        image = productCard.querySelector('.product-card-image')?.src || productCard.querySelector('img')?.src || image;
        const activeSwatch = productCard.querySelector('.swatch-btn.is-active');
        if (activeSwatch) {
          variant = activeSwatch.getAttribute('data-finish') || variant;
          image = activeSwatch.getAttribute('data-image') || image;
          activeImageIndex = activeSwatch.getAttribute('data-image-index');
          variantId = activeSwatch.getAttribute('data-variant-id') || variantId;
        } else {
          variant = productCard.getAttribute('data-product-variant') || variant;
        }
        hasSwatches = true;
        url = productCard.getAttribute('data-product-url') || productCard.querySelector('.product-title-link')?.href || productCard.querySelector('a')?.href || '#';
        if (!variantId) {
          variantId = productCard.getAttribute('data-variant-id') || '';
        }
        const foundSwatches = productCard.querySelectorAll('.product-swatches .swatch-btn');
        if (foundSwatches.length > 0) {
          cardSwatches = Array.from(foundSwatches);
        }
        const imgAttr = productCard.getAttribute('data-product-images');
        if (imgAttr) {
          try {
            images = JSON.parse(imgAttr);
          } catch (e) {
            console.warn('Could not parse data-product-images:', e);
          }
        }
        if (images.length === 0 && cardSwatches) {
          cardSwatches.forEach(cs => {
            const sImg = cs.getAttribute('data-image');
            if (sImg && !images.includes(sImg)) images.push(sImg);
          });
        }
      } else if (dualCard) {
        id = dualCard.getAttribute('data-product-id') || id;
        title = dualCard.querySelector('.dual-title')?.textContent?.replace(/\s+/g, ' ')?.trim() || title;
        price = dualCard.querySelector('.product-price')?.textContent?.trim() || '$' + (dualCard.getAttribute('data-product-price') || '499.00');
        image = dualCard.querySelector('img')?.src || image;
        hasSwatches = false;
        url = dualCard.querySelector('a')?.href || '#';
        if (id.includes('as07')) {
          subtitle = 'Record Player Stand · Enclosed Cabinet Edition';
          variant = 'Enclosed Cabinet';
        } else {
          subtitle = 'Record Player Stand · Open Minimalist Frame';
          variant = 'Open Minimalist Frame';
        }
        images = [image, 'assets/images/lifestyle-turntable-corner.jpg', 'assets/images/product-picasso-walnut.jpg'];
      } else if (as07Content || section?.id === 'as07') {
        id = as07Content?.getAttribute('data-product-id') || 'eiffel-as07';
        title = as07Content?.querySelector('.callout-name')?.textContent?.trim() || 'Eiffel AS07';
        subtitle = as07Content?.querySelector('.callout-desc')?.textContent?.trim() || 'Record Player Stand For Compact Spaces';
        price = '$' + (as07Content?.getAttribute('data-product-price') || '499.00');
        image = section?.querySelector('.as07-main-img')?.src || 'assets/images/as07-lifestyle-main.jpg';
        variant = 'Compact Architecture';
        hasSwatches = false;
        url = as07Content?.querySelector('a')?.href || '#';
        images = [image, 'assets/images/dual-as07-enclosed.jpg', 'assets/images/lifestyle-turntable-corner.jpg'];
      } else if (rs10Content || section?.id === 'rs10') {
        id = rs10Content?.getAttribute('data-product-id') || 'eiffel-rs10';
        title = rs10Content?.querySelector('.callout-name')?.textContent?.trim() || 'Eiffel RS10';
        subtitle = 'Record Player Stand · Microcement Edition';
        price = '$' + (rs10Content?.getAttribute('data-product-price') || '549.00');
        image = section?.querySelector('.rs10-cement-wrap img')?.src || 'assets/images/rs10-cement-main.jpg';
        variant = 'Microcement Gray';
        hasSwatches = true;
        url = rs10Content?.querySelector('a')?.href || '#';
        images = [image, 'assets/images/lifestyle-turntable-corner.jpg', 'assets/images/as07-lifestyle-main.jpg'];
      }

      openQuickView({ id, title, subtitle, price, image, variant, hasSwatches, url, variantId, cardSwatches, images, activeImageIndex }, btn);
    });
  });

  if (!modal.dataset.quickviewInit) {
    modal.dataset.quickviewInit = 'true';
    const closeBtn = modal.querySelector('.modal-close-btn');
    closeBtn?.addEventListener('click', closeQuickViewModal);

    // Close when clicking modal backdrop
    modal.addEventListener('click', (e) => {
      const rect = modal.getBoundingClientRect();
      const isInDialog = (
        rect.top <= e.clientY &&
        e.clientY <= rect.top + rect.height &&
        rect.left <= e.clientX &&
        e.clientX <= rect.left + rect.width
      );
      if (!isInDialog) {
        closeQuickViewModal();
      }
    });

    modal.addEventListener('close', () => {
      document.body.classList.remove('no-scroll');
      document.documentElement.classList.remove('has-modal');
      if (lastActiveElement && typeof lastActiveElement.focus === 'function') {
        lastActiveElement.focus();
      }
    });
  }
}

/* === Module: cart-drawer.js === */
/**
 * Cart Drawer & State Module
 * Manages cart items, badges, toast notifications, drawer slide-out,
 * and dynamic shipping calculation.
 */
let cartManagerInstance = null;

const KNOWN_STORE_VARIANTS = {
  'eiffel-rs05-record-player-stand': {
    default: 51633092100461,
    walnut: 51633092100461,
    black: 51914101719405
  },
  'eiffel-as07-record-player-stand': {
    default: 39533817233469,
    walnut: 39533817233469,
    black: 40092837871677,
    maple: 40092837838909,
    oak: 40092837838909
  },
  'eiffel-v2-as07-record-player-stand-enclosed': {
    default: 51854970093933,
    walnut: 51854970093933
  },
  'eiffel-rs10-record-player-stand': {
    default: 51643471692141,
    walnut: 51643471692141,
    cement: 51643471692141,
    gray: 51643471692141
  }
};

async function resolveNumericVariantId(card, isFromModal) {
  let vId = card?.getAttribute('data-variant-id');
  if (vId && /^\d+$/.test(vId)) return vId;

  const inputVal = card?.querySelector('input[name="id"]')?.value;
  if (inputVal && /^\d+$/.test(inputVal)) return inputVal;

  const activeSwatch = card?.querySelector('.swatch-btn.is-active');
  const swatchVid = activeSwatch?.getAttribute('data-variant-id');
  if (swatchVid && /^\d+$/.test(swatchVid)) return swatchVid;

  const prodUrl = card?.getAttribute('data-product-url') || card?.querySelector('a')?.href || '';
  const prodId = (card?.getAttribute('data-product-id') || '').toLowerCase();
  const prodName = (card?.getAttribute('data-product-name') || '').toLowerCase();
  const variantTitle = (
    isFromModal
      ? (card?.getAttribute('data-product-variant') || activeSwatch?.getAttribute('data-finish') || '')
      : (activeSwatch?.getAttribute('data-finish') || card?.getAttribute('data-product-variant') || '')
  ).toLowerCase();

  for (const [handle, mapping] of Object.entries(KNOWN_STORE_VARIANTS)) {
    if (prodUrl.includes(handle) || prodId.includes(handle) || prodName.includes(handle.replace(/-/g, ' '))) {
      for (const [finishKey, finishId] of Object.entries(mapping)) {
        if (finishKey !== 'default' && variantTitle.includes(finishKey)) {
          return String(finishId);
        }
      }
      return String(mapping.default);
    }
  }

  if (typeof window !== 'undefined' && window.Shopify && prodUrl && prodUrl.includes('/products/')) {
    try {
      const cleanPath = prodUrl.split('?')[0].replace(/\/$/, '');
      const res = await fetch(`${cleanPath}.js`);
      if (res.ok) {
        const prod = await res.json();
        if (prod && prod.variants && prod.variants.length > 0) {
          if (variantTitle) {
            const found = prod.variants.find(v =>
              v.title.toLowerCase().includes(variantTitle) ||
              variantTitle.includes(v.title.toLowerCase())
            );
            if (found) return String(found.id);
          }
          return String(prod.variants[0].id);
        }
      }
    } catch (_) {}
  }

  return null;
}

class CartManager {
  constructor() {
    this.items = [
      {
        id: 'picasso-rs05-walnut',
        name: 'Picasso RS05',
        subtitle: 'Record Player Stand',
        variant: 'American Walnut',
        price: 499.0,
        qty: 1,
        image: 'assets/images/product-picasso-walnut.jpg'
      }
    ];

    this.drawer = document.getElementById('cartDrawer');
    if (this.drawer && this.drawer.parentElement !== document.body) {
      document.body.appendChild(this.drawer);
    }
    this.cartBackdrop = document.querySelector('.cart-backdrop');
    this.cartCloseBtn = document.querySelector('.cart-close-btn');
    this.cartCountBadges = document.querySelectorAll('.cart-count-badge');
    this.cartItemsList = document.querySelector('.cart-items-list');
    this.subtotalEl = document.querySelector('.cart-subtotal-amount');
    this.toastContainer = document.getElementById('toastContainer');
    this.shippingBar = document.querySelector('.progress-bar-fill');
    this.shippingMsg = document.querySelector('.cart-free-shipping span');
    this.checkoutBtn = document.querySelector('.cart-panel-footer .btn');

    this.init();
  }

  init() {
    this.bindDrawerControls();
    this.bindTriggers(document);
    this.render();

    // Shopify live cart count synchronization
    if (typeof window !== 'undefined' && window.Shopify) {
      const rootUrl = (window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(`${rootUrl}cart.js`)
        .then(res => res.json())
        .then(cart => {
          if (cart && typeof cart.item_count === 'number') {
            this.cartCountBadges.forEach((badge) => {
              badge.textContent = cart.item_count;
            });
          }
        })
        .catch(() => {});
    }

    document.addEventListener('cart:change', (event) => {
      const count = event.detail?.cart?.item_count;
      if (typeof count === 'number') {
        this.cartCountBadges.forEach((badge) => {
          badge.textContent = count;
          badge.classList.remove('animate-pop');
          void badge.offsetWidth;
          badge.classList.add('animate-pop');
        });
      }
    });

    document.addEventListener('cart:refresh', () => {
      const rootUrl = (typeof window !== 'undefined' && window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(`${rootUrl}cart.js`)
        .then(res => res.json())
        .then(cart => {
          if (cart && typeof cart.item_count === 'number') {
            this.cartCountBadges.forEach((badge) => {
              badge.textContent = cart.item_count;
            });
          }
        })
        .catch(() => {});
    });
  }

  bindDrawerControls() {
    if (this._drawerControlsBound) return;
    this._drawerControlsBound = true;

    this.cartBackdrop?.addEventListener('click', () => this.close());
    this.cartCloseBtn?.addEventListener('click', () => this.close());

    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        if (this.drawer?.classList.contains('is-open')) {
          this.close();
        }
        const themeCartDrawer = document.getElementById('cart-drawer');
        if (themeCartDrawer && typeof themeCartDrawer.hide === 'function' && themeCartDrawer.hasAttribute('open')) {
          themeCartDrawer.hide();
        }
      }
    });

    this.checkoutBtn?.addEventListener('click', () => {
      if (this.items.length > 0) {
        this.showToast('Proceeding to secure checkout...');
      }
    });

    window.addEventListener('editorial:search', (e) => {
      const q = e.detail?.query;
      if (q) {
        this.showToast(`Searching for "${q}"...`);
      }
    });
  }

  bindTriggers(container) {
    const root = (container && container.querySelectorAll) ? container : document;

    // Open Cart buttons
    const openBtns = root.querySelectorAll('[data-open-cart]');
    openBtns.forEach((btn) => {
      if (btn.dataset.cartOpenInit === 'true') return;
      btn.dataset.cartOpenInit = 'true';
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    // Add to cart buttons
    const atcBtns = root.querySelectorAll('[data-add-to-cart]');
    atcBtns.forEach((btn) => {
      if (btn.dataset.atcInit === 'true') return;
      btn.dataset.atcInit = 'true';
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const isFromModal = !!btn.closest('.modal-info');
        const modal = document.getElementById('quickViewModal');
        const card = btn.closest('.product-card') || btn.closest('.modal-info');
        const id = card?.getAttribute('data-product-id') || 'picasso-rs05-custom';
        const name = card?.getAttribute('data-product-name') || 'Picasso RS05';
        const variant = isFromModal
          ? (card?.getAttribute('data-product-variant') || card?.querySelector('.swatch-btn.is-active')?.getAttribute('data-finish') || 'American Walnut')
          : (card?.querySelector('.swatch-btn.is-active')?.getAttribute('data-finish') || 'Standard Edition');
        const price = parseFloat(card?.getAttribute('data-product-price') || '499.00');
        const img = isFromModal
          ? (modal?.querySelector('.modal-product-img')?.src || 'assets/images/product-picasso-walnut.jpg')
          : (card?.querySelector('.product-card-image')?.src || card?.querySelector('img')?.src || 'assets/images/product-picasso-walnut.jpg');

        const numericVariantId = await resolveNumericVariantId(card, isFromModal);
        let addedToShopify = false;
        const rootUrl = (typeof window !== 'undefined' && window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

        if (numericVariantId) {
          btn.disabled = true;
          const origText = btn.innerHTML;
          btn.innerHTML = `<span class="cart-btn-spinner" style="display:inline-block;width:12px;height:12px;border:2px solid currentColor;border-right-color:transparent;border-radius:50%;animation:spin 0.6s linear infinite;vertical-align:middle;margin-right:6px;"></span>ADDING...`;

          try {
            let sectionsToBundle = ['cart-drawer'];
            document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
              bubbles: true,
              detail: { sections: sectionsToBundle }
            }));

            const formData = new FormData();
            formData.append('id', String(numericVariantId));
            formData.append('quantity', '1');
            formData.append('sections', sectionsToBundle.join(','));
            formData.append('sections_url', `${rootUrl}variants/${numericVariantId}`);

            const response = await fetch(`${rootUrl}cart/add.js`, {
              method: 'POST',
              headers: {
                'X-Requested-With': 'XMLHttpRequest'
              },
              body: formData
            });

            if (response.ok) {
              const responseJson = await response.json();
              addedToShopify = true;

              if (isFromModal) {
                closeQuickViewModal();
              }

              let cartContent = {};
              try {
                const cartRes = await fetch(`${rootUrl}cart.js`);
                cartContent = await cartRes.json();
              } catch (_) {}

              if (responseJson && responseJson.sections) {
                cartContent["sections"] = responseJson["sections"];
              }

              document.dispatchEvent(new CustomEvent('variant:add', {
                bubbles: true,
                detail: {
                  items: responseJson.hasOwnProperty('items') ? responseJson['items'] : [responseJson],
                  cart: cartContent
                }
              }));

              document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
                bubbles: true,
                detail: {
                  baseEvent: 'variant:add',
                  cart: cartContent
                }
              }));

              document.dispatchEvent(new CustomEvent('cart:refresh'));

              const themeCartDrawer = document.getElementById('cart-drawer');
              if (themeCartDrawer && typeof themeCartDrawer.show === 'function') {
                themeCartDrawer.show();
              }

              btn.classList.add('is-added');
              btn.innerHTML = `✓ ADDED`;
              setTimeout(() => {
                btn.disabled = false;
                btn.classList.remove('is-added');
                btn.innerHTML = origText;
              }, 1800);
            }
          } catch (err) {
            console.warn('Shopify cart add failed, using fallback:', err);
          } finally {
            btn.disabled = false;
          }
        }

        if (!addedToShopify) {
          this.addItem({ id, name, variant, price, image: img });

          if (isFromModal) {
            closeQuickViewModal();
          }

          btn.classList.add('is-added');
          const origText = btn.innerHTML;
          btn.innerHTML = `✓ ADDED`;
          setTimeout(() => {
            btn.classList.remove('is-added');
            btn.innerHTML = origText;
          }, 1800);

          this.showToast(`Added ${name} (${variant}) to bag`);
          this.open();
        }
      });
    });
  }

  open() {
    const themeCartDrawer = document.getElementById('cart-drawer');
    if (themeCartDrawer && typeof themeCartDrawer.show === 'function') {
      themeCartDrawer.show();
      return;
    }
    if (!this.drawer) return;
    closeDrawer();
    this.lastActiveTrigger = document.activeElement;
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('no-scroll');
    document.documentElement.classList.add('has-modal');
    setTimeout(() => this.cartCloseBtn?.focus(), 50);
  }

  close() {
    const themeCartDrawer = document.getElementById('cart-drawer');
    if (themeCartDrawer && typeof themeCartDrawer.hide === 'function' && themeCartDrawer.hasAttribute('open')) {
      themeCartDrawer.hide();
    }
    if (!this.drawer) return;
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('no-scroll');
    document.documentElement.classList.remove('has-modal');
    if (this.lastActiveTrigger && typeof this.lastActiveTrigger.focus === 'function') {
      this.lastActiveTrigger.focus();
    }
  }

  addItem(item) {
    const existing = this.items.find((i) => i.id === item.id && i.variant === item.variant);
    if (existing) {
      existing.qty += 1;
    } else {
      this.items.push({ ...item, qty: 1 });
    }
    this.render();
  }

  updateQty(index, delta) {
    if (this.items[index]) {
      this.items[index].qty += delta;
      if (this.items[index].qty <= 0) {
        this.items.splice(index, 1);
      }
      this.render();
    }
  }

  render() {
    const totalCount = this.items.reduce((sum, i) => sum + i.qty, 0);
    const subtotal = this.items.reduce((sum, i) => sum + i.price * i.qty, 0);

    // Update badges with pop animation
    this.cartCountBadges.forEach((badge) => {
      badge.textContent = totalCount;
      badge.classList.remove('animate-pop');
      void badge.offsetWidth;
      badge.classList.add('animate-pop');
    });

    // Update Subtotal
    if (this.subtotalEl) {
      this.subtotalEl.textContent = `$${subtotal.toFixed(2)}`;
    }

    // Dynamic Free Shipping Progress ($500 threshold)
    const freeShippingThreshold = 500.0;
    if (this.shippingBar && this.shippingMsg) {
      if (subtotal >= freeShippingThreshold) {
        this.shippingBar.style.width = '100%';
        this.shippingMsg.textContent = '✓ You qualify for complimentary freight delivery';
      } else if (subtotal > 0) {
        const percent = Math.min(100, Math.round((subtotal / freeShippingThreshold) * 100));
        this.shippingBar.style.width = `${percent}%`;
        const remaining = (freeShippingThreshold - subtotal).toFixed(2);
        this.shippingMsg.textContent = `Add $${remaining} more for complimentary freight delivery`;
      } else {
        this.shippingBar.style.width = '0%';
        this.shippingMsg.textContent = 'Complimentary freight delivery on orders over $500';
      }
    }

    // Checkout button state
    if (this.checkoutBtn) {
      if (this.items.length === 0) {
        this.checkoutBtn.setAttribute('disabled', 'true');
      } else {
        this.checkoutBtn.removeAttribute('disabled');
      }
    }

    // Render Items
    if (this.cartItemsList) {
      if (this.items.length === 0) {
        this.cartItemsList.innerHTML = `
          <div class="text-center" style="padding: 4rem 1rem; color: var(--color-text-muted);">
            <p style="font-size: 1.125rem; margin-bottom: 1rem;">Your shopping bag is empty.</p>
            <p style="font-size: 0.875rem;">Explore our vinyl furniture edit to curate your sound space.</p>
          </div>
        `;
      } else {
        this.cartItemsList.innerHTML = this.items
          .map(
            (item, idx) => `
            <div class="cart-item">
              <img src="${item.image}" alt="${item.name}" class="cart-item-thumb">
              <div class="cart-item-details">
                <span class="cart-item-title">${item.name}</span>
                <span class="cart-item-variant">${item.variant}</span>
                <span class="cart-item-price">$${item.price.toFixed(2)}</span>
                <div class="cart-qty-ctrl">
                  <button type="button" class="cart-qty-btn" data-qty-change="${idx}" data-delta="-1" aria-label="Decrease quantity">−</button>
                  <span class="cart-qty-num">${item.qty}</span>
                  <button type="button" class="cart-qty-btn" data-qty-change="${idx}" data-delta="1" aria-label="Increase quantity">+</button>
                </div>
              </div>
              <button type="button" class="cart-remove-btn" data-remove="${idx}" aria-label="Remove item" style="color: var(--color-text-muted); font-size: 1.1rem; padding: 0.5rem;">✕</button>
            </div>
          `
          )
          .join('');

        // Attach listeners for qty & remove buttons
        this.cartItemsList.querySelectorAll('[data-qty-change]').forEach((btn) => {
          btn.addEventListener('click', () => {
            const idx = parseInt(btn.getAttribute('data-qty-change'));
            const delta = parseInt(btn.getAttribute('data-delta'));
            this.updateQty(idx, delta);
          });
        });

        this.cartItemsList.querySelectorAll('[data-remove]').forEach((btn) => {
          btn.addEventListener('click', () => {
            const idx = parseInt(btn.getAttribute('data-remove'));
            this.items.splice(idx, 1);
            this.render();
          });
        });
      }
    }
  }

  showToast(message) {
    if (!this.toastContainer) return;
    const toast = document.createElement('div');
    toast.className = 'toast';
    toast.innerHTML = `<span>●</span><span>${message}</span>`;
    this.toastContainer.appendChild(toast);

    requestAnimationFrame(() => {
      toast.classList.add('is-visible');
    });

    setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 400);
    }, 3200);
  }
}

function initCartDrawer(container) {
  if (!cartManagerInstance) {
    cartManagerInstance = new CartManager();
  } else if (container) {
    cartManagerInstance.bindTriggers(container);
  }
  return cartManagerInstance;
}

/* === Module: carousel.js === */
/**
 * Carousel Module
 * Powers the Hero Crossfade Slider & the "EVERY LISTENS DIFFERENTLY" Horizontal Gallery
 */
function initCarousels(container) {
  const root = (container && container.querySelectorAll) ? container : document;

  // 1. HERO SLIDER
  const heroContainer = root.querySelector ? root.querySelector('.section-hero') : document.querySelector('.section-hero');
  if (heroContainer && !heroContainer.dataset.carouselInit) {
    heroContainer.dataset.carouselInit = 'true';
    const heroSlides = heroContainer.querySelectorAll('.hero-slide');
    const heroDots = heroContainer.querySelectorAll('.hero-dot');
    let currentHeroIndex = 0;
    let heroInterval = null;

    function showHeroSlide(index) {
      if (!heroSlides.length) return;
      currentHeroIndex = (index + heroSlides.length) % heroSlides.length;

      heroSlides.forEach((s, idx) => {
        s.classList.toggle('is-active', idx === currentHeroIndex);
      });

      heroDots.forEach((d, idx) => {
        const isActive = idx === currentHeroIndex;
        d.classList.toggle('is-active', isActive);
        d.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    function startHeroAutoRotate() {
      stopHeroAutoRotate();
      heroInterval = setInterval(() => {
        showHeroSlide(currentHeroIndex + 1);
      }, 6000);
    }

    function stopHeroAutoRotate() {
      if (heroInterval) clearInterval(heroInterval);
    }

    if (heroSlides.length > 1) {
      heroDots.forEach((dot, idx) => {
        dot.addEventListener('click', () => {
          showHeroSlide(idx);
          startHeroAutoRotate();
        });
      });

      heroContainer.addEventListener('mouseenter', stopHeroAutoRotate);
      heroContainer.addEventListener('mouseleave', startHeroAutoRotate);

      // Touch swipe gesture for Hero slider on mobile devices
      let touchStartX = 0;
      heroContainer.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0]?.clientX || 0;
      }, { passive: true });
      heroContainer.addEventListener('touchend', (e) => {
        const touchEndX = e.changedTouches[0]?.clientX || 0;
        const diff = touchEndX - touchStartX;
        if (Math.abs(diff) > 40) {
          if (diff < 0) {
            showHeroSlide(currentHeroIndex + 1);
          } else {
            showHeroSlide(currentHeroIndex - 1);
          }
          startHeroAutoRotate();
        }
      }, { passive: true });
    }
  }

  // 2. "EVERY LISTENS DIFFERENTLY" HORIZONTAL GALLERY
  const galleryViewports = Array.from(root.querySelectorAll ? root.querySelectorAll('.gallery-carousel-viewport') : document.querySelectorAll('.gallery-carousel-viewport'));
  galleryViewports.forEach((galleryViewport) => {
    if (!galleryViewport || galleryViewport.dataset.carouselInit) return;
    const gallerySection = galleryViewport.closest('.section-gallery');
    const galleryTrack = galleryViewport.querySelector('.gallery-track');
    const initialCards = Array.from(galleryViewport.querySelectorAll('.gallery-card'));

    if (initialCards.length && galleryTrack) {
      galleryViewport.dataset.carouselInit = 'true';
      galleryViewport.style.scrollbarWidth = 'none';
      galleryViewport.style.msOverflowStyle = 'none';

      const galleryContainer = galleryViewport.closest('.section-gallery') || gallerySection || galleryViewport.parentElement;
      const galleryStage = galleryViewport.closest('.gallery-stage') || galleryViewport;
      const galleryPrevBtn = galleryStage?.querySelector('.gallery-nav-prev') || galleryContainer?.querySelector('.gallery-nav-prev');
      const galleryNextBtn = galleryStage?.querySelector('.gallery-nav-next') || galleryContainer?.querySelector('.gallery-nav-next');
      const galleryDots = Array.from(galleryContainer?.querySelectorAll('.gallery-dot') || []);
      const originalCount = initialCards.length;

      // Clone sets for infinite single-direction looping:
      // On desktop: [beforeSet, originalSet, afterSet1, afterSet2]
      // On mobile (<= 768px): [originalSet, afterSet1, afterSet2] (no beforeSet so Card 1 starts at track beginning with zero left-peek)
      const isMobile = window.innerWidth <= 768;
      const beforeFrag = document.createDocumentFragment();
      const afterFrag1 = document.createDocumentFragment();
      const afterFrag2 = document.createDocumentFragment();

      initialCards.forEach((card) => {
        if (!isMobile) {
          const cloneBefore = card.cloneNode(true);
          cloneBefore.setAttribute('aria-hidden', 'true');
          cloneBefore.dataset.clone = 'before';
          beforeFrag.appendChild(cloneBefore);
        }

        const cloneAfter1 = card.cloneNode(true);
        cloneAfter1.setAttribute('aria-hidden', 'true');
        cloneAfter1.dataset.clone = 'after-1';
        afterFrag1.appendChild(cloneAfter1);

        const cloneAfter2 = card.cloneNode(true);
        cloneAfter2.setAttribute('aria-hidden', 'true');
        cloneAfter2.dataset.clone = 'after-2';
        afterFrag2.appendChild(cloneAfter2);
      });

      if (!isMobile) {
        galleryTrack.insertBefore(beforeFrag, initialCards[0]);
      }
      galleryTrack.appendChild(afterFrag1);
      galleryTrack.appendChild(afterFrag2);

      // Initialize videos inside the gallery (original + cloned)
      function setupGalleryVideos() {
        const videos = galleryTrack.querySelectorAll('video');
        videos.forEach((video) => {
          if (video.hasAttribute('autoplay')) {
            video.muted = true;
            const playPromise = video.play();
            if (playPromise !== undefined) {
              playPromise.catch(() => {});
            }
          }

          const card = video.closest('.gallery-card');
          if (card && !card.dataset.videoInit) {
            card.dataset.videoInit = 'true';
            const poster = card.querySelector('[data-poster-overlay]');
            const muteBtn = card.querySelector('.gallery-video-mute-btn');

            if (poster) {
              poster.addEventListener('click', (e) => {
                e.stopPropagation();
                poster.style.display = 'none';
                video.play().catch(() => {});
              });
            }

            if (muteBtn) {
              muteBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                video.muted = !video.muted;
                muteBtn.classList.toggle('is-unmuted', !video.muted);
                const muteIcon = muteBtn.querySelector('.icon-mute');
                const unmuteIcon = muteBtn.querySelector('.icon-unmute');
                if (muteIcon && unmuteIcon) {
                  muteIcon.style.display = video.muted ? 'block' : 'none';
                  unmuteIcon.style.display = video.muted ? 'none' : 'block';
                }
              });
            }
          }
        });
      }

      setupGalleryVideos();

      const resumeVideos = () => {
        const videos = galleryTrack.querySelectorAll('video');
        videos.forEach((v) => {
          if (v.hasAttribute('autoplay') && v.paused) {
            v.play().catch(() => {});
          }
        });
      };
      galleryViewport.addEventListener('click', resumeVideos, { once: true });
      galleryViewport.addEventListener('touchstart', resumeVideos, { once: true, passive: true });

      let stepDistance = 432;
      let singleSetWidth = 432 * originalCount;

      function isMobileViewport() {
        return window.innerWidth <= 768;
      }

      function measureMetrics() {
        const firstCard = initialCards[0];
        const cardWidth = firstCard?.offsetWidth || (isMobileViewport() ? Math.round((window.innerWidth - 40) / 1.2) : 416);
        const style = window.getComputedStyle(galleryTrack);
        const gap = parseFloat(style.columnGap || style.gap) || (isMobileViewport() ? 20 : 16);
        stepDistance = cardWidth + gap;
        singleSetWidth = stepDistance * originalCount;
      }

      measureMetrics();

      // Initialize viewport scroll position to the start of the original set
      // On mobile: strictly 0 (no before-clones, Card 1 aligned at left margin with zero left-peek)
      // On desktop: start of original set (past beforeSet)
      if (isMobileViewport()) {
        galleryViewport.scrollLeft = 0;
      } else if (singleSetWidth > 0) {
        const firstCard = initialCards[0];
        galleryViewport.scrollLeft = firstCard ? (firstCard.offsetLeft || singleSetWidth) : singleSetWidth;
      }

      window.addEventListener('resize', () => {
        measureMetrics();
        if (isMobileViewport() && galleryViewport.scrollLeft < 10) {
          galleryViewport.scrollLeft = 0;
          currentScroll = 0;
        }
      }, { passive: true });

      // Autoplay state & controls
      // Dual-Mode Scrolling: Continuous Marquee ('continuous') vs Step Carousel ('step')
      let isHovered = false;
      let isInteracting = false;
      let autoplayTimer = null;
      let marqueeRAF = null;
      let lastTimestamp = null;
      let currentScroll = galleryViewport.scrollLeft;

      const getScrollMode = () => galleryContainer?.dataset.scrollMode || gallerySection?.dataset.scrollMode || 'continuous';

      function scrollGallery(direction) {
        measureMetrics();
        if (stepDistance <= 0) return;
        galleryViewport.scrollBy({ left: stepDistance * direction, behavior: 'smooth' });
        setTimeout(() => {
          currentScroll = galleryViewport.scrollLeft;
        }, 350);
      }

      function startContinuousScroll() {
        if (isMobileViewport()) {
          // On mobile, continuous pixel drift is replaced with smooth step carousel
          startStepScroll();
          return;
        }
        stopContinuousScroll();
        lastTimestamp = null;
        currentScroll = galleryViewport.scrollLeft;

        function tick(timestamp) {
          if (!lastTimestamp) lastTimestamp = timestamp;
          const delta = Math.min((timestamp - lastTimestamp) / 1000, 0.1);
          lastTimestamp = timestamp;

          if (!isHovered && !isInteracting && singleSetWidth > 0) {
            const speedMultiplier = parseFloat(galleryContainer?.dataset.marqueeSpeed || gallerySection?.dataset.marqueeSpeed || '2') || 2;
            const pixelsPerSecond = 35 * speedMultiplier; // default ~70px/sec at 2x
            currentScroll += pixelsPerSecond * delta;

            if (currentScroll >= 2 * singleSetWidth) {
              currentScroll -= singleSetWidth;
            }
            galleryViewport.scrollLeft = currentScroll;
          }

          marqueeRAF = requestAnimationFrame(tick);
        }

        marqueeRAF = requestAnimationFrame(tick);
      }

      function stopContinuousScroll() {
        if (marqueeRAF) {
          cancelAnimationFrame(marqueeRAF);
          marqueeRAF = null;
        }
        lastTimestamp = null;
      }

      function startStepScroll() {
        stopStepScroll();
        const intervalSec = parseInt(galleryContainer?.dataset.stepInterval || gallerySection?.dataset.stepInterval || '4', 10) || 4;
        autoplayTimer = setInterval(() => {
          if (!isHovered && !isInteracting) {
            scrollGallery(1); // Single direction: forward
          }
        }, intervalSec * 1000);
      }

      function stopStepScroll() {
        if (autoplayTimer) {
          clearInterval(autoplayTimer);
          autoplayTimer = null;
        }
      }

      let isGalleryVisible = false;

      function startActiveScroll() {
        if (!isGalleryVisible) return;
        const isAutoplayDisabled = galleryContainer?.dataset.autoplay === 'false' || gallerySection?.dataset.autoplay === 'false' || getScrollMode() === 'none';
        if (isAutoplayDisabled) return;

        if (isMobileViewport()) {
          // Mobile always uses step carousel auto-advance to play nice with touch & scroll-snap
          stopContinuousScroll();
          startStepScroll();
          return;
        }

        if (getScrollMode() === 'step') {
          stopContinuousScroll();
          startStepScroll();
        } else {
          stopStepScroll();
          startContinuousScroll();
        }
      }

      function stopActiveScroll() {
        stopStepScroll();
        stopContinuousScroll();
      }

      // Hover pause ONLY on actual gallery stage (cards & arrows)
      galleryStage?.addEventListener('mouseenter', () => {
        isHovered = true;
        stopActiveScroll();
      });

      galleryStage?.addEventListener('mouseleave', () => {
        isHovered = false;
        startActiveScroll();
      });

      // Arrow button click handling
      galleryPrevBtn?.addEventListener('click', () => {
        scrollGallery(-1);
        if (!isHovered) startActiveScroll();
      });

      galleryNextBtn?.addEventListener('click', () => {
        scrollGallery(1);
        if (!isHovered) startActiveScroll();
      });

      // Mouse drag-to-scroll functionality
      let isDown = false;
      let startX = 0;
      let scrollLeftStart = 0;

      galleryViewport.addEventListener('mousedown', (e) => {
        isDown = true;
        isInteracting = true;
        stopActiveScroll();
        galleryViewport.style.cursor = 'grabbing';
        startX = e.pageX - galleryViewport.offsetLeft;
        scrollLeftStart = galleryViewport.scrollLeft;
      });

      const stopDrag = () => {
        if (isDown) {
          isDown = false;
          galleryViewport.style.cursor = 'grab';
          currentScroll = galleryViewport.scrollLeft;
          setTimeout(() => {
            isInteracting = false;
            if (!isHovered) startActiveScroll();
          }, 400);
        }
      };

      galleryViewport.addEventListener('mouseleave', stopDrag);
      galleryViewport.addEventListener('mouseup', stopDrag);
      window.addEventListener('mouseup', stopDrag);

      galleryViewport.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - galleryViewport.offsetLeft;
        const walk = (x - startX) * 1.5;
        galleryViewport.scrollLeft = scrollLeftStart - walk;
        currentScroll = galleryViewport.scrollLeft;
      });

      // Touch drag handling for mobile
      galleryViewport.addEventListener('touchstart', () => {
        isInteracting = true;
        stopActiveScroll();
      }, { passive: true });

      galleryViewport.addEventListener('touchend', () => {
        currentScroll = galleryViewport.scrollLeft;
        setTimeout(() => {
          isInteracting = false;
          if (!isHovered) startActiveScroll();
        }, 800);
      }, { passive: true });

      // Seamless infinite loop normalization & active dot sync
      let scrollDebounce = null;

      function checkLoopBoundaries() {
        if (singleSetWidth <= 0) return;
        const hasBeforeClone = Boolean(galleryTrack.querySelector('[data-clone="before"]'));

        if (hasBeforeClone) {
          // Desktop with beforeSet: wrap when past 2 sets or back near 0
          if (galleryViewport.scrollLeft >= 2 * singleSetWidth) {
            galleryViewport.scrollLeft -= singleSetWidth;
            currentScroll = galleryViewport.scrollLeft;
          } else if (galleryViewport.scrollLeft <= 50) {
            galleryViewport.scrollLeft += singleSetWidth;
            currentScroll = galleryViewport.scrollLeft;
          }
        } else {
          // Mobile without beforeSet (starts at 0): wrap forward when past 1 set
          if (galleryViewport.scrollLeft >= singleSetWidth) {
            galleryViewport.scrollLeft -= singleSetWidth;
            currentScroll = galleryViewport.scrollLeft;
          }
        }
      }

      let currentActiveDotIndex = -1;
      function updateActiveDots() {
        if (!galleryDots.length || singleSetWidth <= 0) return;
        const hasBeforeClone = Boolean(galleryTrack.querySelector('[data-clone="before"]'));
        const baseOffset = hasBeforeClone ? singleSetWidth : 0;
        const relativeLeft = galleryViewport.scrollLeft - baseOffset;
        const normalized = ((relativeLeft % singleSetWidth) + singleSetWidth) % singleSetWidth;
        const fraction = normalized / singleSetWidth;
        const activeDotIndex = Math.min(
          galleryDots.length - 1,
          Math.floor(fraction * galleryDots.length + 0.15)
        );

        if (currentActiveDotIndex === activeDotIndex) return;
        currentActiveDotIndex = activeDotIndex;

        galleryDots.forEach((dot, i) => {
          const isActive = i === activeDotIndex;
          if (dot.classList.contains('is-active') !== isActive) {
            dot.classList.toggle('is-active', isActive);
            dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
          }
        });
      }

      galleryViewport.addEventListener('scroll', () => {
        updateActiveDots();
        clearTimeout(scrollDebounce);
        scrollDebounce = setTimeout(() => {
          checkLoopBoundaries();
        }, 120);
      }, { passive: true });

      // Click dot to navigate to corresponding card group
      let dotClickTimer = null;
      galleryDots.forEach((dot, idx) => {
        dot.addEventListener('click', () => {
          measureMetrics();
          stopActiveScroll();
          clearTimeout(dotClickTimer);
          galleryDots.forEach((d, i) => {
            const isActive = i === idx;
            d.classList.toggle('is-active', isActive);
            d.setAttribute('aria-selected', isActive ? 'true' : 'false');
          });
          const hasBeforeClone = Boolean(galleryTrack.querySelector('[data-clone="before"]'));
          const baseOffset = hasBeforeClone ? singleSetWidth : 0;
          const targetOffset = baseOffset + (idx * (singleSetWidth / galleryDots.length));
          currentScroll = targetOffset;
          galleryViewport.scrollTo({ left: targetOffset, behavior: 'smooth' });
          dotClickTimer = setTimeout(() => {
            if (!isHovered) startActiveScroll();
          }, 1200);
        });
      });

      // Observe gallery visibility to pause marquee when off-screen (saving CPU & GPU)
      if (typeof IntersectionObserver !== 'undefined') {
        const galleryTarget = gallerySection || galleryContainer || galleryViewport;
        const galleryObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            isGalleryVisible = entry.isIntersecting;
            if (isGalleryVisible) {
              if (!isHovered && !isInteracting) startActiveScroll();
            } else {
              stopActiveScroll();
            }
          });
        }, { threshold: 0.05 });
        if (galleryTarget) {
          galleryObserver.observe(galleryTarget);
        }
      } else {
        isGalleryVisible = true;
        startActiveScroll();
      }
    }
  });

  // 3. THE LISTENING EDIT PRODUCT CAROUSEL
  const productLayout = root.querySelector ? root.querySelector('[data-product-layout]') : document.querySelector('[data-product-layout]');
  if (productLayout && !productLayout.dataset.productCarouselInit) {
    productLayout.dataset.productCarouselInit = 'true';
    const viewport = productLayout.querySelector('[data-product-carousel-viewport]');
    const track = productLayout.querySelector('[data-product-carousel-track]');
    const pagination = productLayout.querySelector('[data-product-carousel-pagination]');

    if (viewport && track && pagination) {
      viewport.scrollLeft = 0;
      const cards = Array.from(track.children).filter(el => el.classList.contains('product-card') || el.classList.contains('product-card-editorial'));
      if (cards.length > 1) {
        let dots = Array.from(pagination.querySelectorAll('.product-carousel-dot'));
        if (dots.length !== cards.length) {
          pagination.innerHTML = '';
          cards.forEach((card, idx) => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = `product-carousel-dot ${idx === 0 ? 'is-active' : ''}`;
            dot.setAttribute('role', 'tab');
            dot.setAttribute('aria-label', `Product ${idx + 1}`);
            dot.setAttribute('aria-selected', idx === 0 ? 'true' : 'false');
            pagination.appendChild(dot);
          });
          dots = Array.from(pagination.children);
        }

        dots.forEach((dot, idx) => {
          dot.addEventListener('click', () => {
            const card = cards[idx];
            if (card) {
              const targetLeft = idx === 0 ? 0 : card.offsetLeft - 20;
              viewport.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' });
            }
          });
        });
        let scrollTimer;
        viewport.addEventListener('scroll', () => {
          clearTimeout(scrollTimer);
          scrollTimer = setTimeout(() => {
            const scrollCenter = viewport.scrollLeft + viewport.clientWidth / 2;
            let closestIdx = 0;
            let minDiff = Infinity;
            cards.forEach((card, idx) => {
              const cardCenter = card.offsetLeft + card.clientWidth / 2;
              const diff = Math.abs(scrollCenter - cardCenter);
              if (diff < minDiff) {
                minDiff = diff;
                closestIdx = idx;
              }
            });
            dots.forEach((d, i) => {
              const isActive = i === closestIdx;
              d.classList.toggle('is-active', isActive);
              d.setAttribute('aria-selected', isActive ? 'true' : 'false');
            });
          }, 40);
        }, { passive: true });
      }
    }
  }
}

/* === Module: tabs.js === */
/**
 * Tabs Module
 * Accessible tab switcher for Technical Specifications & Craftsmanship
 */
function initTabs(container) {
  const root = (container && container.querySelectorAll) ? container : document;
  const tabContainers = root.querySelectorAll('[data-tabs]');

  tabContainers.forEach((tabsEl) => {
    if (tabsEl.dataset.tabsInit === 'true') return;
    tabsEl.dataset.tabsInit = 'true';

    const tabButtons = tabsEl.querySelectorAll('[role="tab"]');
    const tabPanels = tabsEl.querySelectorAll('[role="tabpanel"]');

    tabButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const targetId = btn.getAttribute('aria-controls');

        // Update button states
        tabButtons.forEach((b) => {
          b.classList.remove('is-active');
          b.setAttribute('aria-selected', 'false');
          b.setAttribute('tabindex', '-1');
        });

        btn.classList.add('is-active');
        btn.setAttribute('aria-selected', 'true');
        btn.setAttribute('tabindex', '0');

        // Update panels
        tabPanels.forEach((panel) => {
          if (panel.id === targetId) {
            panel.classList.add('is-active');
            panel.removeAttribute('hidden');
          } else {
            panel.classList.remove('is-active');
            panel.setAttribute('hidden', '');
          }
        });
      });

      // Keyboard navigation with arrow keys
      btn.addEventListener('keydown', (e) => {
        const buttons = Array.from(tabButtons);
        const index = buttons.indexOf(btn);

        let nextIndex = null;
        if (e.key === 'ArrowRight') {
          nextIndex = (index + 1) % buttons.length;
        } else if (e.key === 'ArrowLeft') {
          nextIndex = (index - 1 + buttons.length) % buttons.length;
        } else if (e.key === 'Home') {
          nextIndex = 0;
        } else if (e.key === 'End') {
          nextIndex = buttons.length - 1;
        }

        if (nextIndex !== null) {
          e.preventDefault();
          buttons[nextIndex].focus();
          buttons[nextIndex].click();
        }
      });
    });
  });
}

/* === Module: accordion.js === */
/**
 * Accordion Module
 * Smooth animated FAQ and info drawers
 */
function initAccordions(container) {
  const root = (container && container.querySelectorAll) ? container : document;
  const accordionItems = root.querySelectorAll('.accordion-item');

  accordionItems.forEach((item) => {
    const header = item.querySelector('.accordion-header');
    if (!header || header.dataset.accordionInit === 'true') return;
    header.dataset.accordionInit = 'true';

    header.addEventListener('click', () => {
      const isOpen = item.classList.contains('is-open');

      // Optional single-open behavior: close siblings if desired
      const listContainer = item.closest('.editorial-faq') || item.parentElement || root;
      const siblings = listContainer.querySelectorAll('.accordion-item');
      siblings.forEach((other) => {
        if (other !== item && other.classList.contains('is-open')) {
          other.classList.remove('is-open');
          const otherBtn = other.querySelector('.accordion-header');
          otherBtn?.setAttribute('aria-expanded', 'false');
        }
      });

      if (isOpen) {
        item.classList.remove('is-open');
        header.setAttribute('aria-expanded', 'false');
      } else {
        item.classList.add('is-open');
        header.setAttribute('aria-expanded', 'true');
      }
    });
  });
}

/* === Module: swatches.js === */
/**
 * Swatches Module
 * Handles dynamic finish selection and thumbnail swaps for product cards and modal dialogs
 */
function initSwatches(container) {
  const root = (container && container.querySelectorAll) ? container : document;

  // 1. Product Cards Swatches
  const productCards = root.querySelectorAll('.product-card');

  productCards.forEach((card) => {
    if (card.dataset.swatchesInit === 'true') return;
    card.dataset.swatchesInit = 'true';

    const swatches = card.querySelectorAll('.swatch-btn');
    const imageEl = card.querySelector('.product-card-image');
    const subtitleEl = card.querySelector('.product-subtitle');
    const priceEl = card.querySelector('.product-price');
    const hiddenIdInput = card.querySelector('input[name="id"]');
    const mediaLink = card.querySelector('.product-card-media-link');
    const titleLink = card.querySelector('.product-title-link');

    swatches.forEach((swatch) => {
      swatch.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Active state toggle
        swatches.forEach((s) => s.classList.remove('is-active'));
        swatch.classList.add('is-active');

        const finishName = swatch.getAttribute('data-finish') || '';
        const finishImage = swatch.getAttribute('data-image');
        const finishPrice = swatch.getAttribute('data-price');
        const finishPriceRaw = swatch.getAttribute('data-price-raw');
        const variantId = swatch.getAttribute('data-variant-id');
        const variantUrl = swatch.getAttribute('data-variant-url');

        if (finishImage && imageEl) {
          imageEl.src = finishImage;
          imageEl.style.opacity = '0.7';
          requestAnimationFrame(() => {
            imageEl.style.opacity = '1';
          });
        }

        if (subtitleEl && finishName) {
          subtitleEl.textContent = `Record Player Stand · ${finishName}`;
        }

        if (priceEl && finishPrice) {
          priceEl.textContent = finishPrice;
        }

        if (hiddenIdInput && variantId) {
          hiddenIdInput.value = variantId;
        }

        if (variantUrl) {
          if (mediaLink) mediaLink.href = variantUrl;
          if (titleLink) titleLink.href = variantUrl;
          card.setAttribute('data-product-url', variantUrl);
        } else if (variantId && mediaLink && mediaLink.href && mediaLink.href !== '#') {
          try {
            const u = new URL(mediaLink.href, window.location.origin);
            u.searchParams.set('variant', variantId);
            mediaLink.href = u.toString();
            if (titleLink) titleLink.href = u.toString();
            card.setAttribute('data-product-url', u.toString());
          } catch (_) {}
        }

        if (finishName) card.setAttribute('data-product-variant', finishName);
        if (finishPriceRaw) card.setAttribute('data-product-price', finishPriceRaw);
        if (variantId) card.setAttribute('data-variant-id', variantId);
      });
    });
  });

  // 2. Quick View Modal Swatches
  const modalSwatchesWrap = document.querySelector('.modal-swatches');
  if (modalSwatchesWrap && !modalSwatchesWrap.dataset.swatchesInit) {
    modalSwatchesWrap.dataset.swatchesInit = 'true';
    const modalInfo = document.querySelector('.modal-info');
    const modalImg = document.querySelector('.modal-product-img');
    const modalSubtitle = document.querySelector('.modal-product-subtitle');
    const modalPrice = document.querySelector('.modal-product-price');
    const modalViewLink = document.querySelector('.modal-view-link');
    const modalTitleLink = document.querySelector('.modal-title-link');
    const modalImageLink = document.querySelector('.modal-image-link');
    const modalSwatches = modalSwatchesWrap.querySelectorAll('.swatch-btn');

    modalSwatches.forEach((swatch) => {
      swatch.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        modalSwatches.forEach((s) => s.classList.remove('is-active'));
        swatch.classList.add('is-active');

        const finishName = swatch.getAttribute('data-finish') || '';
        const finishImage = swatch.getAttribute('data-image');
        const finishImageIndex = swatch.getAttribute('data-image-index');
        const finishPrice = swatch.getAttribute('data-price');
        const finishPriceRaw = swatch.getAttribute('data-price-raw');
        const variantId = swatch.getAttribute('data-variant-id');
        const variantUrl = swatch.getAttribute('data-variant-url');

        if (typeof goToImage === 'function') {
          goToImage(finishImage, finishImageIndex);
        } else if (finishImage && modalImg) {
          modalImg.src = finishImage;
          modalImg.style.opacity = '0.7';
          requestAnimationFrame(() => {
            modalImg.style.opacity = '1';
          });
        }

        if (modalSubtitle && finishName) {
          modalSubtitle.textContent = `Record Player Stand · ${finishName}`;
        }

        if (modalPrice && finishPrice) {
          modalPrice.textContent = finishPrice;
        }

        if (modalInfo) {
          if (finishName) modalInfo.setAttribute('data-product-variant', finishName);
          if (finishPriceRaw) modalInfo.setAttribute('data-product-price', finishPriceRaw);
          if (variantId) modalInfo.setAttribute('data-variant-id', variantId);
        }

        if (variantUrl) {
          if (modalViewLink) modalViewLink.href = variantUrl;
          if (modalTitleLink) modalTitleLink.href = variantUrl;
          if (modalImageLink) modalImageLink.href = variantUrl;
        } else if (variantId && modalViewLink && modalViewLink.href && modalViewLink.href !== '#') {
          try {
            const u = new URL(modalViewLink.href, window.location.origin);
            u.searchParams.set('variant', variantId);
            modalViewLink.href = u.toString();
            if (modalTitleLink) modalTitleLink.href = u.toString();
            if (modalImageLink) modalImageLink.href = u.toString();
          } catch (_) {}
        }
      });
    });
  }
}

/* === Module: filters.js === */
/**
 * Collection Filters Module
 * Powers category filter pills for "THE LISTENING EDIT"
 */
function initFilters(container) {
  const root = (container && container.querySelectorAll) ? container : document;
  const filterBtns = root.querySelectorAll(".filter-btn");
  const productCards = document.querySelectorAll(".product-grid-4 .product-card");

  if (!filterBtns.length || !productCards.length) return;

  filterBtns.forEach((btn) => {
    if (btn.dataset.filterInit === 'true') return;
    btn.dataset.filterInit = 'true';

    btn.addEventListener("click", () => {
      filterBtns.forEach((b) => b.classList.remove("is-active"));
      btn.classList.add("is-active");

      const filterValue = btn.getAttribute("data-filter") || "all";

      productCards.forEach((card) => {
        const categories = (card.getAttribute("data-category") || "").split(" ");
        if (filterValue === "all" || categories.includes(filterValue)) {
          card.classList.remove("is-hidden");
          card.style.opacity = "0";
          card.style.transform = "translateY(8px)";
          requestAnimationFrame(() => {
            card.style.transition = "opacity 300ms ease, transform 300ms ease";
            card.style.opacity = "1";
            card.style.transform = "translateY(0)";
          });
        } else {
          card.classList.add("is-hidden");
        }
      });
    });
  });
}


  /* === Module: editorial-motion.js === */
  /**
   * FITUEYES Editorial Landing — Motion & Interaction System
   * 
   * 1. 100vh Pinned Viewport Sequential Reveal (Section 6 Showcase Trio)
   * 2. Text Hover Floating Product Capsule with GSAP quickTo inertia
   * 3. Left Floating Capsule Section Navigation with pill indicator morph
   * 4. 10 Core Section Tiered Entrance Animations with reduced-motion support
   */

  function initPinnedShowcase(gsap, ScrollTrigger) {
    const showcase = document.querySelector('.pinned-showcase-section');
    if (!showcase) return;

    const block1 = showcase.querySelector('[data-showcase-step="1"], .dual-col-left');
    const block2 = showcase.querySelector('[data-showcase-step="2"], .dual-col-right');

    const container = showcase.querySelector('.dual-container') || showcase;

    if (!gsap || !ScrollTrigger) {
      [block1, block2].forEach(function(b) {
        if (b) {
          b.style.opacity = '1';
          b.style.visibility = 'visible';
          b.style.transform = 'none';
        }
      });
      return;
    }

    // Check if this ScrollTrigger instance already has triggers registered for showcase
    const hasTrigger = typeof ScrollTrigger.getAll === 'function' &&
      ScrollTrigger.getAll().some(function(t) { return t.trigger === showcase; });
    if (hasTrigger) return;
    showcase.dataset.pinnedInit = 'true';

    const mm = gsap.matchMedia();
    const forceMotion = typeof window !== 'undefined' && Boolean(window.__FORCE_MOTION__ || (window.location && window.location.search && window.location.search.indexOf('motion=1') !== -1));

    mm.add(
      {
        isDesktop: forceMotion
          ? '(min-width: 769px)'
          : '(min-width: 769px) and (prefers-reduced-motion: no-preference)',
        isMobileOrReduced: forceMotion
          ? '(max-width: 768px)'
          : '(max-width: 768px), (prefers-reduced-motion: reduce)',
      },
      function(context) {
        const isDesktop = context.conditions.isDesktop;

        if (isDesktop && block1 && block2) {
          const otherBlocks = Array.from(
            showcase.querySelectorAll('[data-showcase-step]:not([data-showcase-step="1"]), .dual-col-right')
          );
          const uniqueOtherBlocks = [...new Set(otherBlocks)];

          // Ensure no CSS transitions interfere with 1:1 scrollbar scrubbing
          [container, block1, ...uniqueOtherBlocks].forEach(function(el) {
            if (el) {
              el.style.transition = 'none';
              el.style.opacity = '1';
              el.style.visibility = 'visible';
            }
          });

          let cachedB1Bottom = 0;
          function measureB1Bottom() {
            let el = block1;
            let top = 0;
            while (el && el !== showcase) {
              top += el.offsetTop;
              el = el.offsetParent;
            }
            cachedB1Bottom = top + (block1 ? block1.offsetHeight : 0);
            return cachedB1Bottom;
          }
          measureB1Bottom();
          ScrollTrigger.addEventListener('refreshInit', measureB1Bottom);

          // Trigger 1: Left block (Block 1 / AS07) entrance & rapid bottom-contact exit
          // Bound to scroll with smooth 0.6s sub-pixel interpolation & force3D hardware acceleration:
          // In forward scroll: translates up (y: b1ExitY -> 0) as it enters, reaching y: 0 when its bottom contacts screen bottom
          // In reverse scroll: after Block 2 exits & section unpins, when Block 1's bottom contacts screen bottom,
          // it rapidly moves DOWN (y: 0 -> b1ExitY) to exit with high velocity and unmistakable visual difference.
          // NO opacity / transparency effects (不加透明效果)
          const b1ExitY = 600;
          const b1ExitDist = 380;

          gsap.fromTo(
            block1,
            { y: b1ExitY, force3D: true },
            {
              y: 0,
              ease: 'none',
              force3D: true,
              scrollTrigger: {
                trigger: showcase,
                start: function() {
                  const b1Bottom = cachedB1Bottom || measureB1Bottom();
                  const touchBottom = window.innerHeight - b1Bottom;
                  return 'top ' + (touchBottom + b1ExitDist) + 'px';
                },
                end: function() {
                  const b1Bottom = cachedB1Bottom || measureB1Bottom();
                  const touchBottom = window.innerHeight - b1Bottom;
                  return 'top ' + touchBottom + 'px';
                },
                scrub: 0.6,
                invalidateOnRefresh: true,
                fastScrollEnd: true,
              },
            }
          );

          // Trigger 2: Pinned timeline for Block 2 (RS05) entrance, dwell, and unpin
          // start: 'top top' -> Section locks (pin: true)
          // Stage 1: Block 2 enters from bottom (y: 600 -> 0) with 100% opacity
          // Stage 2: Dwell hold - both blocks in place, section locked & motionless
          // Stage 3: Release buffer - section unpins and scrolls away into next section (blocks do not vanish)
          // In reverse scroll:
          // Re-entering section -> Section locks at 'top top' -> Dwell -> Block 2 moves down to y: 600 -> Section immediately UNPINS!
          const tl = gsap.timeline({
            scrollTrigger: {
              trigger: showcase,
              start: 'top top',
              end: '+=1800',
              pin: true,
              scrub: 0.6, // Smooth sub-pixel interpolation for silky 60fps/120fps motion
              anticipatePin: 1,
              invalidateOnRefresh: true,
              fastScrollEnd: true,
            },
          });

          // Block 2 entrance from bottom (y: 600 -> 0), strictly linear, NO opacity fade, force3D hardware accelerated
          if (uniqueOtherBlocks.length > 0) {
            uniqueOtherBlocks.forEach(function(b) {
              tl.fromTo(
                b,
                { y: 600, force3D: true },
                { y: 0, duration: 0.45, ease: 'none', force3D: true },
                0
              );
            });
          }

          // Dwell window - All blocks in place, section locked and still
          tl.to({}, { duration: 0.50 });

          // Release buffer - blocks DO NOT vanish, section scrolls away as a whole
          tl.to({}, { duration: 0.05 });
        } else {
          // Mobile or reduced motion: make both blocks normally visible without pin
          [block1, block2].forEach(function(b) {
            if (b) {
              gsap.set(b, { clearProps: 'all', autoAlpha: 1, y: 0, x: 0, scale: 1 });
            }
          });
          if (container) {
            gsap.set(container, { clearProps: 'transform' });
          }
        }
      }
    );
  }

  function initCapsuleHover(gsap) {
    let capsule = document.getElementById('floatingProductCapsule');
    if (!capsule) {
      capsule = document.createElement('div');
      capsule.id = 'floatingProductCapsule';
      capsule.className = 'floating-product-cutout';
      capsule.setAttribute('aria-hidden', 'true');
      capsule.innerHTML = [
        '<img src="" alt="" class="cutout-img">',
        '<span class="capsule-product-title" style="display:none;"></span>'
      ].join('');
      document.body.appendChild(capsule);
    }

    const cutoutImg = capsule.querySelector('.cutout-img');
    const titleEl = capsule.querySelector('.capsule-product-title');

    let xTo = null;
    let yTo = null;
    if (gsap) {
      gsap.set(capsule, { xPercent: -50, yPercent: -50 });
      xTo = gsap.quickTo(capsule, 'x', { duration: 0.35, ease: 'power2.out' });
      yTo = gsap.quickTo(capsule, 'y', { duration: 0.35, ease: 'power2.out' });
    }

    let hideTimer = null;
    let touchDismissTimer = null;
    let isCutoutActive = false;

    const showCutout = function(trigger, clientX, clientY) {
      clearTimeout(hideTimer);
      clearTimeout(touchDismissTimer);

      const img = trigger.dataset.capsuleImg;
      const title = trigger.dataset.capsuleTitle;

      if (!img) return;

      if (cutoutImg && cutoutImg.getAttribute('src') !== img) {
        cutoutImg.src = img;
      }
      if (titleEl && title) titleEl.textContent = title;

      const section = trigger.closest('section') || document.body;
      if (capsule.parentElement !== section || (section.firstChild && section.firstChild !== capsule)) {
        if (section.firstChild) {
          section.insertBefore(capsule, section.firstChild);
        } else {
          section.appendChild(capsule);
        }
      }

      const rect = section.getBoundingClientRect();
      const relX = clientX - rect.left;
      let relY = clientY - rect.top;
      if (window.innerWidth <= 768) {
        relY = Math.max(40, relY - 45);
      }

      if (gsap) {
        if (!isCutoutActive) {
          gsap.set(capsule, { x: relX, y: relY, scale: 0.85, autoAlpha: 0 });
          gsap.to(capsule, {
            autoAlpha: 1,
            scale: 1,
            duration: 0.3,
            ease: 'power2.out',
            overwrite: 'auto',
          });
          isCutoutActive = true;
        } else {
          if (xTo && yTo) {
            xTo(relX);
            yTo(relY);
          }
          gsap.to(capsule, {
            autoAlpha: 1,
            scale: 1,
            duration: 0.2,
            ease: 'power2.out',
            overwrite: 'auto',
          });
        }
      } else {
        capsule.style.opacity = '1';
        capsule.style.visibility = 'visible';
        capsule.style.transform = 'translate(calc(' + relX + 'px - 50%), calc(' + relY + 'px - 50%)) scale(1)';
        isCutoutActive = true;
      }
    };

    const moveCutout = function(trigger, clientX, clientY) {
      const section = trigger.closest('section') || document.body;
      const rect = section.getBoundingClientRect();
      const relX = clientX - rect.left;
      let relY = clientY - rect.top;
      if (window.innerWidth <= 768) {
        relY = Math.max(40, relY - 45);
      }

      if (xTo && yTo) {
        xTo(relX);
        yTo(relY);
      } else {
        capsule.style.transform = 'translate(calc(' + relX + 'px - 50%), calc(' + relY + 'px - 50%)) scale(1)';
      }
    };

    const hideCutoutImmediate = function() {
      clearTimeout(hideTimer);
      clearTimeout(touchDismissTimer);
      if (gsap) {
        gsap.to(capsule, {
          autoAlpha: 0,
          scale: 0.85,
          duration: 0.22,
          ease: 'power2.in',
          overwrite: 'auto',
          onComplete: function() {
            isCutoutActive = false;
          },
        });
      } else {
        capsule.style.opacity = '0';
        capsule.style.visibility = 'hidden';
        isCutoutActive = false;
      }
    };

    const scheduleHideCutout = function() {
      clearTimeout(hideTimer);
      hideTimer = setTimeout(function() {
        hideCutoutImmediate();
      }, 70);
    };

    const triggers = document.querySelectorAll('.capsule-hover-trigger');
    triggers.forEach(function(trigger) {
      const img = (trigger.dataset.capsuleImg || '').trim();
      if (!img) return;
      if (trigger.dataset.capsuleInit === 'true') return;
      trigger.dataset.capsuleInit = 'true';

      trigger.addEventListener('mouseenter', function(e) {
        showCutout(trigger, e.clientX, e.clientY);
      });

      trigger.addEventListener('mousemove', function(e) {
        moveCutout(trigger, e.clientX, e.clientY);
      });

      trigger.addEventListener('mouseleave', scheduleHideCutout);

      // Touch tap support for mobile devices
      trigger.addEventListener('touchend', function(e) {
        const touch = e.changedTouches && e.changedTouches[0];
        if (touch) {
          showCutout(trigger, touch.clientX, touch.clientY);
          touchDismissTimer = setTimeout(hideCutoutImmediate, 2400);
        }
      }, { passive: true });
    });

    // Tap outside dismissal & scroll dismissal for touch devices
    if (!window.__CAPSULE_OUTSIDE_TAP_INIT__) {
      window.__CAPSULE_OUTSIDE_TAP_INIT__ = true;
      document.addEventListener('touchstart', function(e) {
        if (!e.target.closest('.capsule-hover-trigger') && !e.target.closest('#floatingProductCapsule')) {
          hideCutoutImmediate();
        }
      }, { passive: true });

      window.addEventListener('scroll', function() {
        if (isCutoutActive && window.innerWidth <= 768) {
          hideCutoutImmediate();
        }
      }, { passive: true });
    }
  }

  /* === Module: page-nav-indicator.js (zhishitiao.md) === */
  var DEFAULT_NAV_CONFIG = {
    position: 'left',
    mode: 'dots',
    offset: 24,
    headerMode: 'smart',
    storageKey: 'page-indicator-config'
  };

  var VALID_POSITIONS = ['left', 'right', 'top', 'bottom'];
  var VALID_MODES = ['dots', 'text', 'expand'];
  var VALID_HEADER_MODES = ['fixed', 'smart', 'static'];

  function PageNavIndicator(options) {
    options = options || {};
    this.storageKey = options.storageKey || DEFAULT_NAV_CONFIG.storageKey;
    this.sectionSelector = options.sectionSelector || '[data-page-section]';
    this.rawStoredConfig = {};
    this.config = this.loadConfig(options);

    this.root = null;
    this.sections = [];
    this.activeId = null;
    this.isExpanded = false;

    this.isProgrammaticScroll = false;
    this.programmaticTimer = null;
    this.scrollTicking = false;

    this._lastNavigatedHash = null;
    this._lastNavigatedTime = 0;

    var self = this;
    this.handleScroll = this.onScroll.bind(this);
    this.handleResize = this.onResize.bind(this);
    this.handlePopState = this.onPopState.bind(this);
    this.handleHashChange = this.onHashChange.bind(this);
    this.handleKeydown = this.onKeydown.bind(this);
    this.handleDocumentClick = this.onDocumentClick.bind(this);
    this.handleDocumentTouch = this.onDocumentTouch.bind(this);
    this.handlePointerDown = this.onPointerDown.bind(this);
    this.handleUserScrollInteraction = this.onUserScrollInteraction.bind(this);

    this.handleRootMouseEnter = function() { self.expand(); };
    this.handleRootMouseLeave = function() { self.collapse(); };
    this.handleRootFocusIn = function() {
      if (self.config.mode === 'expand' && !self._suppressExpandOnFocus) {
        self.expand();
      }
    };
    this.handleRootFocusOut = function(e) {
      if (self.root && !self.root.contains(e.relatedTarget)) {
        self.collapse();
      }
    };
    this._suppressExpandOnFocus = false;
  }

  PageNavIndicator.prototype.loadConfig = function(overrides) {
    var domConfig = {};
    if (typeof document !== 'undefined') {
      var navEl = document.getElementById('page-nav-indicator');
      if (navEl && navEl.dataset) {
        if (navEl.dataset.position) domConfig.position = navEl.dataset.position;
        if (navEl.dataset.mode) domConfig.mode = navEl.dataset.mode;
        if (navEl.dataset.offset) {
          var parsed = parseInt(navEl.dataset.offset, 10);
          if (!isNaN(parsed)) domConfig.offset = parsed;
        }
        if (navEl.dataset.headerMode) domConfig.headerMode = navEl.dataset.headerMode;
        if (typeof navEl.dataset.navTitle !== 'undefined') domConfig.navTitle = navEl.dataset.navTitle;
      }
    }

    var saved = {};
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        var storedStr = window.localStorage.getItem(this.storageKey);
        if (storedStr) {
          var parsed = JSON.parse(storedStr);
          if (parsed && typeof parsed === 'object') {
            saved = parsed;
            this.rawStoredConfig = Object.assign({}, parsed);
          }
        }
      }
    } catch (err) {
      console.warn('[PageNavIndicator] Failed to read localStorage:', err);
    }

    var isDesignMode = (typeof window !== 'undefined' && window.Shopify && window.Shopify.designMode);
    var merged = isDesignMode
      ? Object.assign({}, DEFAULT_NAV_CONFIG, saved, domConfig, overrides || {})
      : Object.assign({}, DEFAULT_NAV_CONFIG, domConfig, saved, overrides || {});

    if (!VALID_POSITIONS.includes(merged.position)) {
      merged.position = DEFAULT_NAV_CONFIG.position;
    }
    if (!VALID_MODES.includes(merged.mode)) {
      merged.mode = DEFAULT_NAV_CONFIG.mode;
    }
    if (!VALID_HEADER_MODES.includes(merged.headerMode)) {
      merged.headerMode = DEFAULT_NAV_CONFIG.headerMode;
    }
    if (typeof merged.offset !== 'number' || isNaN(merged.offset) || merged.offset < 0) {
      merged.offset = DEFAULT_NAV_CONFIG.offset;
    }

    return merged;
  };

  PageNavIndicator.prototype.saveConfig = function() {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        var toSave = Object.assign({}, this.rawStoredConfig, {
          position: this.config.position,
          mode: this.config.mode,
          offset: this.config.offset,
          headerMode: this.config.headerMode,
          storageKey: this.config.storageKey
        });
        window.localStorage.setItem(this.storageKey, JSON.stringify(toSave));
        this.rawStoredConfig = toSave;
      }
    } catch (err) {
      console.warn('[PageNavIndicator] Failed to save localStorage:', err);
    }
  };

  PageNavIndicator.prototype.init = function() {
    var existingEls = document.querySelectorAll('#page-nav-indicator');
    var el = existingEls.length > 0 ? existingEls[existingEls.length - 1] : null;
    if (existingEls.length > 1) {
      for (var i = 0; i < existingEls.length - 1; i++) {
        existingEls[i].remove();
      }
    }
    if (!el) {
      el = document.createElement('nav');
      el.id = 'page-nav-indicator';
      el.className = 'page-nav-indicator';
      el.setAttribute('aria-label', 'Page sections');
      document.body.appendChild(el);
    } else {
      if (el.parentElement !== document.body) {
        document.body.appendChild(el);
      }
      el.setAttribute('aria-label', 'Page sections');
    }

    this.root = el;
    this.refresh();
    return this;
  };

  PageNavIndicator.prototype.collectSections = function() {
    var rawElements = Array.from(document.querySelectorAll(this.sectionSelector || '[data-page-section]'));
    var items = [];

    rawElements.forEach(function(el) {
      if (el.hasAttribute('data-page-nav-disabled') && el.getAttribute('data-page-nav-disabled') !== 'false') return;
      if (el.hasAttribute('hidden') || el.hidden) return;
      if (el.getAttribute('aria-hidden') === 'true') return;
      if (typeof window !== 'undefined') {
        var style = window.getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden') return;
      }

      var id = (el.id || '').trim();
      var isExistingDomIdColliding = id && document.getElementById(id) && document.getElementById(id) !== el;
      if (!id || isExistingDomIdColliding || items.some(function(item) { return item.id === id; })) {
        var base = id || ('page-section-' + (items.length + 1));
        var candidate = base;
        var counter = 1;
        while ((document.getElementById(candidate) && document.getElementById(candidate) !== el) || items.some(function(item) { return item.id === candidate; })) {
          candidate = base + '-' + (counter++);
        }
        id = candidate;
        el.id = id;
      }

      var rawLabel = (el.getAttribute('data-page-label') || '').trim();
      var label = rawLabel.replace(/[\s\u00A0\u200B\uFEFF]/g, '') ? rawLabel : '';
      if (!label) {
        var headings = Array.from(el.querySelectorAll('h1, h2, h3'));
        for (var h = 0; h < headings.length; h++) {
          var txt = (headings[h].textContent || '').trim().replace(/\s+/g, ' ');
          if (txt.replace(/[\s\u00A0\u200B\uFEFF]/g, '')) {
            label = txt;
            break;
          }
        }
      }
      if (!label) {
        label = 'Section ' + (items.length + 1);
      }

      items.push({
        id: id,
        label: label,
        element: el
      });
    });

    this.sections = items;
    this.measureSectionTops();
    return items;
  };

  PageNavIndicator.prototype.measureSectionTops = function() {
    if (!this.sections) return;
    for (var i = 0; i < this.sections.length; i++) {
      var sec = this.sections[i];
      if (sec.element) {
        sec.top = sec.element.getBoundingClientRect().top + window.scrollY;
      }
    }
  };

  PageNavIndicator.prototype.applyClasses = function() {
    if (!this.root) return;

    this.root.className = 'page-nav-indicator';
    if (this.config.position === 'left') {
      this.root.classList.add('left-capsule-nav');
    }

    var isVertical = ['left', 'right'].includes(this.config.position);
    this.root.classList.add(isVertical ? 'style-vertical' : 'style-horizontal');
    this.root.classList.add('pos-' + this.config.position);
    this.root.classList.add('mode-' + this.config.mode);

    if (this.root.getAttribute('data-mobile') === 'false') {
      this.root.classList.add('hide-on-mobile');
    } else {
      this.root.classList.remove('hide-on-mobile');
    }

    if (this.isExpanded && this.config.mode === 'expand') {
      this.root.classList.add('is-expanded');
    }

    this.root.style.setProperty('--nav-offset', this.config.offset + 'px');

    if (this.config.position === 'top') {
      this.updateHeaderOffset();
      this.observeHeader();
    } else {
      this.root.style.removeProperty('--header-bottom-offset');
    }
  };

  /**
   * Determine visible bottom edge of any fixed/sticky headers
   */
  PageNavIndicator.prototype.getVisibleHeaderBottom = function() {
    if (typeof document === 'undefined') return 0;
    var headerSelectors = [
      '.editorial-header',
      '.shopify-section--header',
      '#shopify-section-header',
      'store-header',
      'sticky-header',
      '.header-wrapper',
      '.site-header',
      'header.header',
      '.shopify-section--announcement-bar',
      '#shopify-section-announcement-bar',
      '.announcement-bar',
      'body > header',
      'header[role="banner"]'
    ];

    var maxBottom = 0;
    var seen = typeof Set !== 'undefined' ? new Set() : null;

    for (var i = 0; i < headerSelectors.length; i++) {
      var nodes = document.querySelectorAll(headerSelectors[i]);
      for (var j = 0; j < nodes.length; j++) {
        var el = nodes[j];
        if (seen && seen.has(el)) continue;
        if (seen) seen.add(el);

        var rect = el.getBoundingClientRect();
        if (rect.bottom <= 0 || rect.top > 150) continue;

        var style = window.getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') continue;

        var isStickyOrFixed = style.position === 'fixed' || style.position === 'sticky';
        var parent = el.parentElement;
        while (parent && !isStickyOrFixed && parent !== document.body && parent !== document.documentElement) {
          var pStyle = window.getComputedStyle(parent);
          if (pStyle.position === 'fixed' || pStyle.position === 'sticky') {
            isStickyOrFixed = true;
            break;
          }
          parent = parent.parentElement;
        }

        var isAtPageTop = window.scrollY < (rect.height || 100);
        if ((isStickyOrFixed || isAtPageTop) && rect.bottom > maxBottom && rect.bottom < window.innerHeight / 2) {
          maxBottom = rect.bottom;
        }
      }
    }

    return Math.round(maxBottom);
  };

  PageNavIndicator.prototype.updateHeaderOffset = function() {
    if (!this.root || this.config.position !== 'top') return;
    var headerBottom = this.getVisibleHeaderBottom();
    this.root.style.setProperty('--header-bottom-offset', headerBottom + 'px');
  };

  PageNavIndicator.prototype.observeHeader = function() {
    var self = this;
    if (this._headerObserver || typeof MutationObserver === 'undefined') return;

    this._headerObserver = new MutationObserver(function() {
      if (self.config.position === 'top') {
        self.updateHeaderOffset();
      }
    });

    var headerSelectors = [
      '.editorial-header',
      '.shopify-section--header',
      '#shopify-section-header',
      'store-header',
      'sticky-header',
      '.header-wrapper',
      '.site-header',
      'header.header',
      '.shopify-section--announcement-bar',
      '#shopify-section-announcement-bar',
      '.announcement-bar'
    ];

    headerSelectors.forEach(function(sel) {
      document.querySelectorAll(sel).forEach(function(el) {
        self._headerObserver.observe(el, {
          attributes: true,
          attributeFilter: ['class', 'style', 'aria-hidden']
        });
      });
    });
  };

  /**
   * Smoothly auto-scroll active nav item into view
   */
  PageNavIndicator.prototype.scrollActiveItemIntoView = function(smooth) {
    if (typeof smooth === 'undefined') smooth = true;
    if (!this.root) return;
    var navList = this.root.querySelector('.nav-list');
    if (navList) {
      var activeItem = navList.querySelector('.nav-item.is-active, .nav-item.active');
      if (activeItem) {
        var isVertical = this.root.classList.contains('style-vertical') || ['left', 'right'].indexOf(this.config.position) !== -1;

        if (!isVertical && navList.scrollWidth > navList.clientWidth) {
          var itemRect = activeItem.getBoundingClientRect();
          var listRect = navList.getBoundingClientRect();
          var relativeLeft = itemRect.left - listRect.left + navList.scrollLeft;
          var targetScrollLeft = relativeLeft - (navList.clientWidth / 2) + (itemRect.width / 2);
          var maxScrollX = navList.scrollWidth - navList.clientWidth;
          var clampedLeft = Math.max(0, Math.min(targetScrollLeft, maxScrollX));
          
          if (!smooth) {
            var prevBehavior = navList.style.scrollBehavior;
            navList.style.scrollBehavior = 'auto';
            navList.scrollLeft = clampedLeft;
            navList.style.scrollBehavior = prevBehavior;
          } else if (typeof navList.scrollTo === 'function') {
            try {
              navList.scrollTo({ left: clampedLeft, behavior: 'smooth' });
            } catch (err) {
              navList.scrollLeft = clampedLeft;
            }
          } else {
            navList.scrollLeft = clampedLeft;
          }

          if (Math.abs(navList.scrollLeft - clampedLeft) > 2) {
            setTimeout(function() {
              if (navList && Math.abs(navList.scrollLeft - clampedLeft) > 2) {
                navList.scrollLeft = clampedLeft;
              }
            }, 120);
          }
        } else if (isVertical && navList.scrollHeight > navList.clientHeight) {
          var itemRectY = activeItem.getBoundingClientRect();
          var listRectY = navList.getBoundingClientRect();
          var relativeTop = itemRectY.top - listRectY.top + navList.scrollTop;
          var targetScrollTop = relativeTop - (navList.clientHeight / 2) + (itemRectY.height / 2);
          var maxScrollY = navList.scrollHeight - navList.clientHeight;
          var clampedTop = Math.max(0, Math.min(targetScrollTop, maxScrollY));
          if (!smooth) {
            var prevBehaviorY = navList.style.scrollBehavior;
            navList.style.scrollBehavior = 'auto';
            navList.scrollTop = clampedTop;
            navList.style.scrollBehavior = prevBehaviorY;
          } else if (typeof navList.scrollTo === 'function') {
            try {
              navList.scrollTo({ top: clampedTop, behavior: 'smooth' });
            } catch (errY) {
              navList.scrollTop = clampedTop;
            }
          } else {
            navList.scrollTop = clampedTop;
          }
        }
      }
    }

    var dropdown = this.root.querySelector('.expand-dropdown-list');
    if (dropdown && this.isExpanded && dropdown.scrollHeight > dropdown.clientHeight) {
      var activeDropdownItem = dropdown.querySelector('.nav-item.is-active, .nav-item.active');
      if (activeDropdownItem) {
        var dropItemRect = activeDropdownItem.getBoundingClientRect();
        var dropRect = dropdown.getBoundingClientRect();
        var relativeTopDrop = dropItemRect.top - dropRect.top + dropdown.scrollTop;
        var targetScrollTopDrop = relativeTopDrop - (dropdown.clientHeight / 2) + (dropItemRect.height / 2);
        var maxScrollTopDrop = dropdown.scrollHeight - dropdown.clientHeight;
        var clampedDrop = Math.max(0, Math.min(targetScrollTopDrop, maxScrollTopDrop));
        if (!smooth) {
          var prevBehaviorDrop = dropdown.style.scrollBehavior;
          dropdown.style.scrollBehavior = 'auto';
          dropdown.scrollTop = clampedDrop;
          dropdown.style.scrollBehavior = prevBehaviorDrop;
        } else if (typeof dropdown.scrollTo === 'function') {
          dropdown.scrollTo({ top: clampedDrop, behavior: 'smooth' });
        } else {
          dropdown.scrollTop = clampedDrop;
        }
      }
    }
  };

  PageNavIndicator.prototype.renderDOM = function() {
    var self = this;
    if (!this.root) return;
    this.root.innerHTML = '';

    if (this.sections.length === 0) return;

    var navTitle = (this.config.navTitle || this.root.getAttribute('data-nav-title') || '').trim();
    if (navTitle) {
      var header = document.createElement('div');
      header.className = 'nav-bar-header';
      var headerTitle = document.createElement('span');
      headerTitle.className = 'nav-bar-title';
      headerTitle.textContent = navTitle;
      header.appendChild(headerTitle);
      this.root.appendChild(header);
    }

    if (this.config.mode === 'dots') {
      var list = document.createElement('div');
      list.className = 'nav-list left-capsule-dock';

      this.sections.forEach(function(sec) {
        var a = document.createElement('a');
        a.href = '#' + sec.id;
        a.className = 'nav-item capsule-nav-item';
        a.dataset.target = '#' + sec.id;
        a.setAttribute('aria-label', 'Go to ' + sec.label);
        a.setAttribute('role', 'button');
        a.tabIndex = 0;

        var dot = document.createElement('span');
        dot.className = 'nav-dot capsule-nav-dot';

        var tooltip = document.createElement('span');
        tooltip.className = 'nav-label capsule-nav-tooltip';
        tooltip.textContent = sec.label;

        a.appendChild(dot);
        a.appendChild(tooltip);

        a.addEventListener('click', function(e) {
          self.onItemClick(e, sec);
        });
        a.addEventListener('keydown', function(e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            self.onItemClick(e, sec);
          }
        });

        list.appendChild(a);
      });

      this.root.appendChild(list);
    } else if (this.config.mode === 'text') {
      var textList = document.createElement('div');
      textList.className = 'nav-list nav-list-text';

      this.sections.forEach(function(sec) {
        var a = document.createElement('a');
        a.href = '#' + sec.id;
        a.className = 'nav-item nav-item-text';
        a.dataset.target = '#' + sec.id;
        a.setAttribute('aria-label', sec.label);
        a.setAttribute('role', 'button');
        a.tabIndex = 0;

        var dot = document.createElement('span');
        dot.className = 'nav-dot';
        dot.setAttribute('aria-hidden', 'true');

        var label = document.createElement('span');
        label.className = 'nav-label';
        label.textContent = sec.label;

        a.appendChild(dot);
        a.appendChild(label);

        a.addEventListener('click', function(e) {
          self.onItemClick(e, sec);
        });
        a.addEventListener('keydown', function(e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            self.onItemClick(e, sec);
          }
        });

        textList.appendChild(a);
      });

      this.root.appendChild(textList);
    } else if (this.config.mode === 'expand') {
      var card = document.createElement('div');
      card.className = 'expand-card';

      var trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'expand-trigger';
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-controls', 'expand-dropdown-list');
      trigger.setAttribute('aria-label', 'Toggle section navigation');

      var currentLabel = document.createElement('span');
      currentLabel.className = 'expand-current-label';
      var initialSec = this.sections.find(function(s) { return s.id === self.activeId; }) || this.sections[0];
      currentLabel.textContent = initialSec ? initialSec.label : 'Navigation';

      var iconSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      iconSvg.setAttribute('class', 'expand-icon');
      iconSvg.setAttribute('viewBox', '0 0 12 12');
      iconSvg.setAttribute('fill', 'none');
      iconSvg.setAttribute('aria-hidden', 'true');
      var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'M2.5 4.5L6 8L9.5 4.5');
      path.setAttribute('stroke', 'currentColor');
      path.setAttribute('stroke-width', '1.5');
      path.setAttribute('stroke-linecap', 'round');
      path.setAttribute('stroke-linejoin', 'round');
      iconSvg.appendChild(path);

      trigger.appendChild(currentLabel);
      trigger.appendChild(iconSvg);

      var dropdown = document.createElement('div');
      dropdown.id = 'expand-dropdown-list';
      dropdown.className = 'expand-dropdown-list';
      dropdown.setAttribute('role', 'menu');

      this.sections.forEach(function(sec) {
        var a = document.createElement('a');
        a.href = '#' + sec.id;
        a.className = 'nav-item expand-nav-item';
        a.dataset.target = '#' + sec.id;
        a.setAttribute('role', 'menuitem');
        a.setAttribute('aria-label', sec.label);
        a.tabIndex = self.isExpanded ? 0 : -1;

        var dot = document.createElement('span');
        dot.className = 'nav-dot';
        dot.setAttribute('aria-hidden', 'true');

        var label = document.createElement('span');
        label.className = 'nav-label';
        label.textContent = sec.label;

        a.appendChild(dot);
        a.appendChild(label);

        a.addEventListener('click', function(e) {
          self.onItemClick(e, sec);
          self.collapse();
        });
        a.addEventListener('keydown', function(e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            self.onItemClick(e, sec);
            self.collapse();
          }
        });

        dropdown.appendChild(a);
      });

      card.appendChild(trigger);
      card.appendChild(dropdown);
      this.root.appendChild(card);

      trigger.addEventListener('click', function(e) {
        e.stopPropagation();
        if (self.isExpanded) self.collapse();
        else self.expand();
      });

      this.root.addEventListener('mouseenter', this.handleRootMouseEnter);
      this.root.addEventListener('mouseleave', this.handleRootMouseLeave);
      this.root.addEventListener('focusin', this.handleRootFocusIn);
      this.root.addEventListener('focusout', this.handleRootFocusOut);
    }
  };

  PageNavIndicator.prototype.measureExpandHeight = function() {
    if (!this.root || this.config.mode !== 'expand') return 42;

    var trigger = this.root.querySelector('.expand-trigger');
    var dropdown = this.root.querySelector('.expand-dropdown-list');
    var card = this.root.querySelector('.expand-card') || this.root;

    if (!trigger || !dropdown) return 42;

    var wasExpanded = this.root.classList.contains('is-expanded');
    if (!wasExpanded) {
      this.root.classList.add('is-expanded');
    }

    var triggerHeight = (trigger.getBoundingClientRect ? trigger.getBoundingClientRect().height : 0) || trigger.offsetHeight || 40;
    var listHeight = dropdown.scrollHeight || 0;

    var rootComputed = window.getComputedStyle(this.root);
    var rootBorderTop = parseFloat(rootComputed.borderTopWidth) || 0;
    var rootBorderBottom = parseFloat(rootComputed.borderBottomWidth) || 0;
    var rootPaddingTop = parseFloat(rootComputed.paddingTop) || 0;
    var rootPaddingBottom = parseFloat(rootComputed.paddingBottom) || 0;

    var cardComputed = window.getComputedStyle(card);
    var cardBorderTop = parseFloat(cardComputed.borderTopWidth) || 0;
    var cardBorderBottom = parseFloat(cardComputed.borderBottomWidth) || 0;
    var cardPaddingTop = parseFloat(cardComputed.paddingTop) || 0;
    var cardPaddingBottom = parseFloat(cardComputed.paddingBottom) || 0;

    var dropdownComputed = window.getComputedStyle(dropdown);
    var dropdownBorderTop = parseFloat(dropdownComputed.borderTopWidth) || 0;
    var dropdownBorderBottom = parseFloat(dropdownComputed.borderBottomWidth) || 0;

    var totalHeight = Math.ceil(
      triggerHeight +
      listHeight +
      dropdownBorderTop +
      dropdownBorderBottom +
      rootBorderTop +
      rootBorderBottom +
      rootPaddingTop +
      rootPaddingBottom +
      cardBorderTop +
      cardBorderBottom +
      cardPaddingTop +
      cardPaddingBottom
    );

    if (!wasExpanded) {
      this.root.classList.remove('is-expanded');
    }

    var maxAvailable = typeof window !== 'undefined' ? Math.max(120, window.innerHeight - 32) : 600;
    var finalHeight = Math.min(totalHeight, maxAvailable);

    this.root.style.setProperty('--expand-height', finalHeight + 'px');
    return finalHeight;
  };

  PageNavIndicator.prototype.expand = function() {
    if (this.config.mode !== 'expand' || !this.root) return;
    this.root.classList.add('is-expanded');
    this.measureExpandHeight();
    var trigger = this.root.querySelector('.expand-trigger');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    var items = this.root.querySelectorAll('.expand-dropdown-list .nav-item');
    for (var i = 0; i < items.length; i++) {
      items[i].tabIndex = 0;
    }
    this.isExpanded = true;
    this.scrollActiveItemIntoView();
  };

  PageNavIndicator.prototype.collapse = function() {
    if (this.config.mode !== 'expand' || !this.root) return;
    this.root.classList.remove('is-expanded');
    var trigger = this.root.querySelector('.expand-trigger');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    var items = this.root.querySelectorAll('.expand-dropdown-list .nav-item');
    for (var i = 0; i < items.length; i++) {
      items[i].tabIndex = -1;
    }
    this.isExpanded = false;
  };

  PageNavIndicator.prototype.onItemClick = function(e, sec, updateHistory) {
    if (typeof updateHistory === 'undefined') updateHistory = true;
    var self = this;
    if (e) e.preventDefault();
    if (!sec || !sec.element) return;

    this.isProgrammaticScroll = true;
    clearTimeout(this.programmaticTimer);

    this.setActiveSection(sec.id);

    var rawTargetPos = sec.element.getBoundingClientRect().top + window.scrollY;
    var header = document.querySelector('.editorial-header') || document.querySelector('store-header') || document.querySelector('.header') || document.querySelector('header');
    var headerHeight = header ? header.offsetHeight : 0;
    var headerDeduction = 0;

    if (this.config.headerMode === 'fixed') {
      headerDeduction = headerHeight;
    } else if (this.config.headerMode === 'smart') {
      if (rawTargetPos < window.scrollY) {
        headerDeduction = headerHeight;
      } else {
        headerDeduction = 0;
      }
    } else if (this.config.headerMode === 'static') {
      headerDeduction = 0;
    }

    var finalTargetPos = Math.max(0, rawTargetPos - headerDeduction);

    window.scrollTo({
      top: finalTargetPos,
      behavior: 'smooth'
    });

    if (updateHistory) {
      if (window.history && window.history.pushState) {
        if (window.location.hash !== '#' + sec.id) {
          window.history.pushState(null, '', '#' + sec.id);
        }
      } else if (window.location.hash !== '#' + sec.id) {
        window.location.hash = '#' + sec.id;
      }
    }

    this.programmaticTimer = setTimeout(function() {
      self.isProgrammaticScroll = false;
      self.updateActive();
    }, 850);
  };

  PageNavIndicator.prototype.onScroll = function() {
    var self = this;
    if (this.config.position === 'top') {
      this.updateHeaderOffset();
    }
    if (this.isProgrammaticScroll) return;

    if (!this.scrollTicking) {
      window.requestAnimationFrame(function() {
        self.updateActive();
        self.scrollTicking = false;
      });
      this.scrollTicking = true;
    }
  };

  PageNavIndicator.prototype.onResize = function() {
    this.measureSectionTops();
    if (this.config.position === 'top') {
      this.updateHeaderOffset();
    }
    if (this.config.mode === 'expand' && this.isExpanded) {
      this.measureExpandHeight();
    }
    this.updateActive();
    this.scrollActiveItemIntoView();
  };

  PageNavIndicator.prototype.handleUrlChange = function() {
    var rawHash = window.location.hash || '';
    var hash = rawHash.replace('#', '');
    var now = Date.now();

    if (hash === this._lastNavigatedHash && now - this._lastNavigatedTime < 60) {
      return;
    }
    this._lastNavigatedHash = hash;
    this._lastNavigatedTime = now;

    if (hash) {
      var match = this.sections.find(function(s) { return s.id === hash; });
      if (match) {
        this.onItemClick(null, match, false);
      }
    } else if (this.sections.length > 0) {
      this.onItemClick(null, this.sections[0], false);
    }
  };

  PageNavIndicator.prototype.onPopState = function() {
    this.handleUrlChange();
  };

  PageNavIndicator.prototype.onHashChange = function() {
    this.handleUrlChange();
  };

  PageNavIndicator.prototype.onKeydown = function(e) {
    if (e.key === 'Escape' && this.config.mode === 'expand' && this.isExpanded) {
      var self = this;
      this._suppressExpandOnFocus = true;
      this.collapse();
      var trigger = this.root ? this.root.querySelector('.expand-trigger') : null;
      if (trigger) trigger.focus();
      setTimeout(function() {
        self._suppressExpandOnFocus = false;
      }, 150);
    }
  };

  PageNavIndicator.prototype.onUserScrollInteraction = function() {
    if (this.isProgrammaticScroll) {
      this.isProgrammaticScroll = false;
      clearTimeout(this.programmaticTimer);
      this.onScroll();
    }
  };

  PageNavIndicator.prototype.onDocumentClick = function(e) {
    if (this.config.mode === 'expand' && this.isExpanded) {
      if (this.root && !this.root.contains(e.target)) {
        this.collapse();
      }
    }
  };

  PageNavIndicator.prototype.onDocumentTouch = function(e) {
    if (this.config.mode === 'expand' && this.isExpanded) {
      var target = (e.touches && e.touches.length > 0 && e.touches[0].target) ? e.touches[0].target : e.target;
      if (this.root && target && !this.root.contains(target)) {
        this.collapse();
      }
    }
  };

  PageNavIndicator.prototype.onPointerDown = function(e) {
    if (this.config.mode === 'expand' && this.isExpanded) {
      if (this.root && e.target && !this.root.contains(e.target)) {
        this.collapse();
      }
    }
  };

  PageNavIndicator.prototype.updateActive = function(force) {
    if (!force && this.isProgrammaticScroll) return;
    if (!this.sections || this.sections.length === 0) return;

    var header = document.querySelector('.editorial-header') || document.querySelector('header');
    var headerOffset = header && !header.classList.contains('header-hidden') ? header.offsetHeight : 0;
    var scrollPos = window.scrollY + headerOffset + 80;

    var targetSection = this.sections[0];

    if (window.scrollY < 80) {
      targetSection = this.sections[0];
    } else if (window.innerHeight + window.scrollY >= (document.documentElement.scrollHeight - 50)) {
      targetSection = this.sections[this.sections.length - 1];
    } else {
      // Fast memory-cached sequential check - ZERO DOM reads during scroll
      for (var i = 0; i < this.sections.length; i++) {
        var sec = this.sections[i];
        var secTop = typeof sec.top === 'number' ? sec.top : (sec.top = sec.element.getBoundingClientRect().top + window.scrollY);
        if (scrollPos >= secTop) {
          targetSection = sec;
        }
      }
    }

    if (targetSection) {
      this.setActiveSection(targetSection.id, force);
    }

    // Keep active item always visible inside capsule during scroll in text mode
    if (this.config.mode === 'text' && this.root) {
      var navListEl = this.root.querySelector('.nav-list');
      var activeItemEl = navListEl ? navListEl.querySelector('.nav-item.is-active, .nav-item.active') : null;
      if (navListEl && activeItemEl && navListEl.scrollWidth > navListEl.clientWidth) {
        var itemRectB = activeItemEl.getBoundingClientRect();
        var listRectB = navListEl.getBoundingClientRect();
        if (itemRectB.left < listRectB.left - 2 || itemRectB.right > listRectB.right + 2) {
          this.scrollActiveItemIntoView();
        }
      }
    }
  };

  PageNavIndicator.prototype.setActiveSection = function(id, force) {
    if (this.activeId === id && !force) return;
    var self = this;
    this.activeId = id;
    if (!this.root) return;

    var items = Array.from(this.root.querySelectorAll('.nav-item'));
    var activeSec = null;

    items.forEach(function(item) {
      var target = (item.getAttribute('href') || item.dataset.target || '').replace(/^.*#/, '');
      var isActive = target === id;

      item.classList.toggle('active', isActive);
      item.classList.toggle('is-active', isActive);

      if (isActive) {
        item.setAttribute('aria-current', 'true');
        activeSec = self.sections.find(function(s) { return s.id === id; });
      } else {
        item.removeAttribute('aria-current');
      }
    });

    if (this.config.mode === 'expand') {
      var labelEl = this.root.querySelector('.expand-current-label');
      if (labelEl && activeSec) {
        labelEl.textContent = activeSec.label;
      }
    }

    this.scrollActiveItemIntoView();
  };

  PageNavIndicator.prototype.cleanupEvents = function() {
    if (this._headerObserver) {
      this._headerObserver.disconnect();
      this._headerObserver = null;
    }

    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('popstate', this.handlePopState);
    window.removeEventListener('hashchange', this.handleHashChange);
    window.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('wheel', this.handleUserScrollInteraction);
    window.removeEventListener('touchmove', this.handleUserScrollInteraction);
    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('touchstart', this.handleDocumentTouch);
    document.removeEventListener('pointerdown', this.handlePointerDown);

    if (this.root) {
      this.root.removeEventListener('mouseenter', this.handleRootMouseEnter);
      this.root.removeEventListener('mouseleave', this.handleRootMouseLeave);
      this.root.removeEventListener('focusin', this.handleRootFocusIn);
      this.root.removeEventListener('focusout', this.handleRootFocusOut);
    }

    clearTimeout(this.programmaticTimer);
  };

  PageNavIndicator.prototype.bindEvents = function() {
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize, { passive: true });
    window.addEventListener('popstate', this.handlePopState);
    window.addEventListener('hashchange', this.handleHashChange);
    window.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('wheel', this.handleUserScrollInteraction, { passive: true });
    window.addEventListener('touchmove', this.handleUserScrollInteraction, { passive: true });
    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('touchstart', this.handleDocumentTouch, { passive: true });
    document.addEventListener('pointerdown', this.handlePointerDown, { passive: true });

    if (typeof document !== 'undefined' && !this._shopifyThemeListenerAttached) {
      this._shopifyThemeListenerAttached = true;
      var self = this;
      document.addEventListener('shopify:section:load', function(e) {
        var navEl = document.getElementById('page-nav-indicator');
        if (navEl && navEl.dataset) {
          var updated = {};
          if (navEl.dataset.position) updated.position = navEl.dataset.position;
          if (navEl.dataset.mode) updated.mode = navEl.dataset.mode;
          if (navEl.dataset.offset) {
            var o = parseInt(navEl.dataset.offset, 10);
            if (!isNaN(o)) updated.offset = o;
          }
          if (navEl.dataset.headerMode) updated.headerMode = navEl.dataset.headerMode;
          if (typeof navEl.dataset.navTitle !== 'undefined') updated.navTitle = navEl.dataset.navTitle;
          self.updateConfig(updated, false);
        }
        self.refresh();
      });
      document.addEventListener('shopify:section:select', function() {
        self.refresh();
      });
    }
  };

  PageNavIndicator.prototype.refresh = function() {
    var existingEls = document.querySelectorAll('#page-nav-indicator');
    if (existingEls.length > 1) {
      var latest = existingEls[existingEls.length - 1];
      for (var i = 0; i < existingEls.length - 1; i++) {
        existingEls[i].remove();
      }
      this.root = latest;
    } else if (existingEls.length === 1) {
      this.root = existingEls[0];
    }
    if (!this.root) return;

    if (this.root.parentElement !== document.body) {
      document.body.appendChild(this.root);
    }

    this.isExpanded = false;
    this.cleanupEvents();
    this.collectSections();
    this.applyClasses();
    this.renderDOM();
    this.bindEvents();
    this.updateActive(true);
  };

  PageNavIndicator.prototype.updateConfig = function(newConfig, save) {
    newConfig = newConfig || {};
    var prevPosition = this.config.position;
    var prevMode = this.config.mode;
    var self = this;

    Object.keys(newConfig).forEach(function(key) {
      if (!['position', 'mode', 'headerMode', 'offset'].includes(key)) {
        self.rawStoredConfig[key] = newConfig[key];
        self.config[key] = newConfig[key];
      }
    });

    if (newConfig.storageKey) {
      this.storageKey = newConfig.storageKey;
      this.config.storageKey = newConfig.storageKey;
    }

    if (newConfig.position && VALID_POSITIONS.includes(newConfig.position)) {
      this.config.position = newConfig.position;
    }
    if (newConfig.mode && VALID_MODES.includes(newConfig.mode)) {
      this.config.mode = newConfig.mode;
    }
    if (newConfig.headerMode && VALID_HEADER_MODES.includes(newConfig.headerMode)) {
      this.config.headerMode = newConfig.headerMode;
    }
    if (typeof newConfig.offset === 'number' && !isNaN(newConfig.offset) && newConfig.offset >= 0) {
      this.config.offset = newConfig.offset;
    }

    if (save) {
      this.saveConfig();
    }

    if (prevPosition !== this.config.position || prevMode !== this.config.mode) {
      this.refresh();
    } else {
      this.applyClasses();
      this.updateActive(true);
    }
  };

  PageNavIndicator.prototype.getConfig = function() {
    return Object.assign({}, this.config);
  };

  PageNavIndicator.prototype.getSections = function() {
    return this.sections.slice();
  };

  PageNavIndicator.prototype.destroy = function() {
    this.cleanupEvents();
    if (this.root && this.root.parentElement) {
      this.root.remove();
    }
    this.root = null;
    this.sections = [];
    if (typeof window !== 'undefined' && window.pageNavIndicator === this) {
      window.pageNavIndicator = null;
    }
  };

  function initCapsuleNavigation(gsap, ScrollTrigger) {
    if (window.pageNavIndicator && typeof window.pageNavIndicator.refresh === 'function') {
      window.pageNavIndicator.refresh();
      return window.pageNavIndicator;
    }
    var indicator = new PageNavIndicator();
    indicator.init();
    window.pageNavIndicator = indicator;
    window.PageNavIndicator = PageNavIndicator;
    return indicator;
  }

  function initSectionEntrances(gsap, ScrollTrigger) {
    if (!gsap || !ScrollTrigger) return;
    const as07 = document.getElementById('as07');
    const hasTriggers = typeof ScrollTrigger.getAll === 'function' &&
      ScrollTrigger.getAll().some(function(t) { return t.trigger === as07; });
    if (hasTriggers && window.__EDITORIAL_ENTRANCES_INIT__) return;
    window.__EDITORIAL_ENTRANCES_INIT__ = true;

    const mm = gsap.matchMedia();
    const forceMotion = typeof window !== 'undefined' && Boolean(window.__FORCE_MOTION__ || (window.location && window.location.search && window.location.search.indexOf('motion=1') !== -1));

    mm.add(
      {
        noPreference: forceMotion
          ? '(min-width: 1px)'
          : '(prefers-reduced-motion: no-preference)',
        reducedMotion: forceMotion
          ? '(max-width: 0px)'
          : '(prefers-reduced-motion: reduce)',
      },
      function(context) {
        const noPreference = context.conditions.noPreference;

        if (!noPreference) {
          gsap.set(
            [
              '.hero-content',
              '.hero-controls',
              '.as07-editorial-layout > *',
              '.rs10-left-col > *',
              '.rs10-right-col',
              '.quote-text',
              '.gallery-header',
              '.gallery-card',
              '.atmosphere-statement-wrap',
              '.atmosphere-banner',
              '.edit-header',
              '.product-grid-4 .product-card',
              '.specs-header',
              '.specs-tabs-nav',
              '.tab-panel.is-active',
              '.faq-header',
              '.accordion-item',
            ],
            { autoAlpha: 1, y: 0, x: 0 }
          );
          return;
        }

        // 1. Hero Entrance
        gsap.fromTo(
          '.hero-content',
          { y: 35, autoAlpha: 0, force3D: true },
          {
            y: 0,
            autoAlpha: 1,
            duration: 1.1,
            ease: 'power2.out',
            delay: 0.15,
            force3D: true,
            onComplete: function() {
              gsap.set('.hero-content', { clearProps: 'transform' });
            },
          }
        );

        // 2. Section 2: AS07 Feature
        const as07 = document.getElementById('as07');
        if (as07) {
          const targets = as07.querySelectorAll('.as07-main-media, .as07-callout-col, .as07-editorial-col');
          gsap.fromTo(
            targets,
            { y: 40, autoAlpha: 0, force3D: true },
            {
              y: 0,
              autoAlpha: 1,
              duration: 0.85,
              stagger: 0.15,
              ease: 'power2.out',
              force3D: true,
              scrollTrigger: {
                trigger: as07,
                start: 'top 75%',
                toggleActions: 'play none none none',
                once: true,
              },
              onComplete: function() {
                gsap.set(targets, { clearProps: 'transform' });
              },
            }
          );
        }

        // 3. Section 3: RS10 Feature
        const rs10 = document.getElementById('rs10');
        if (rs10) {
          const targets = rs10.querySelectorAll('.rs10-detail-wrap, .rs10-headline, .rs10-product-callout, .rs10-right-col');
          gsap.fromTo(
            targets,
            { y: 40, autoAlpha: 0, force3D: true },
            {
              y: 0,
              autoAlpha: 1,
              duration: 0.85,
              stagger: 0.15,
              ease: 'power2.out',
              force3D: true,
              scrollTrigger: {
                trigger: rs10,
                start: 'top 75%',
                toggleActions: 'play none none none',
                once: true,
              },
              onComplete: function() {
                gsap.set(targets, { clearProps: 'transform' });
              },
            }
          );
        }

        // 4. Section 4: Philosophy Quote
        const quote = document.querySelector('.section-quote-divider');
        if (quote) {
          const target = quote.querySelector('.quote-text');
          gsap.fromTo(
            target,
            { y: 30, autoAlpha: 0, force3D: true },
            {
              y: 0,
              autoAlpha: 1,
              duration: 0.9,
              ease: 'power2.out',
              force3D: true,
              scrollTrigger: {
                trigger: quote,
                start: 'top 80%',
                toggleActions: 'play none none none',
                once: true,
              },
              onComplete: function() {
                gsap.set(target, { clearProps: 'transform' });
              },
            }
          );
        }

        // 5. Section 5: Gallery
        const gallery = document.getElementById('gallery');
        if (gallery) {
          const targets = gallery.querySelectorAll('.gallery-header, .gallery-card');
          gsap.fromTo(
            targets,
            { y: 35, autoAlpha: 0, force3D: true },
            {
              y: 0,
              autoAlpha: 1,
              duration: 0.8,
              stagger: 0.1,
              ease: 'power2.out',
              force3D: true,
              scrollTrigger: {
                trigger: gallery,
                start: 'top 75%',
                toggleActions: 'play none none none',
                once: true,
              },
              onComplete: function() {
                gsap.set(targets, { clearProps: 'transform' });
              },
            }
          );
        }

        // 6. Section 6: Dual Showcase on mobile (desktop is handled by R1 pinned reveal)
        const dual = document.getElementById('dualShowcase');
        if (dual && window.innerWidth <= 768) {
          const targets = dual.querySelectorAll('.dual-col-left, .dual-col-right');
          gsap.fromTo(
            targets,
            { y: 35, autoAlpha: 0, force3D: true },
            {
              y: 0,
              autoAlpha: 1,
              duration: 0.8,
              stagger: 0.12,
              ease: 'power2.out',
              force3D: true,
              scrollTrigger: {
                trigger: dual,
                start: 'top 80%',
                toggleActions: 'play none none none',
                once: true,
              },
              onComplete: function() {
                gsap.set(targets, { clearProps: 'transform' });
              },
            }
          );
        }

        // 7. Section 7: Atmosphere
        const atmosphere = document.getElementById('atmosphere');
        if (atmosphere) {
          const targets = atmosphere.querySelectorAll('.atmosphere-statement-wrap, .atmosphere-banner');
          gsap.fromTo(
            targets,
            { y: 35, autoAlpha: 0, force3D: true },
            {
              y: 0,
              autoAlpha: 1,
              duration: 0.85,
              stagger: 0.15,
              ease: 'power2.out',
              force3D: true,
              scrollTrigger: {
                trigger: atmosphere,
                start: 'top 75%',
                toggleActions: 'play none none none',
                once: true,
              },
              onComplete: function() {
                gsap.set(targets, { clearProps: 'transform' });
              },
            }
          );
        }

        // 8. Section 8: The Listening Edit
        const listeningEdit = document.getElementById('listening-edit');
        if (listeningEdit) {
          const targets = listeningEdit.querySelectorAll('.edit-header, .product-grid-4 .product-card');
          gsap.fromTo(
            targets,
            { y: 35, autoAlpha: 0, force3D: true },
            {
              y: 0,
              autoAlpha: 1,
              duration: 0.8,
              stagger: 0.08,
              ease: 'power2.out',
              force3D: true,
              scrollTrigger: {
                trigger: listeningEdit,
                start: 'top 75%',
                toggleActions: 'play none none none',
                once: true,
              },
              onComplete: function() {
                gsap.set(targets, { clearProps: 'transform' });
              },
            }
          );
        }

        // 9. Section 9: Technical Specs
        const specs = document.getElementById('specs');
        if (specs) {
          const targets = specs.querySelectorAll('.specs-header, .specs-tabs-nav, .tab-panel.is-active');
          gsap.fromTo(
            targets,
            { y: 35, autoAlpha: 0, force3D: true },
            {
              y: 0,
              autoAlpha: 1,
              duration: 0.8,
              stagger: 0.1,
              ease: 'power2.out',
              force3D: true,
              scrollTrigger: {
                trigger: specs,
                start: 'top 75%',
                toggleActions: 'play none none none',
                once: true,
              },
              onComplete: function() {
                gsap.set(targets, { clearProps: 'transform' });
              },
            }
          );
        }

        // 10. Section 10: Curated FAQ
        const faq = document.getElementById('faq');
        if (faq) {
          const targets = faq.querySelectorAll('.faq-header, .accordion-item');
          gsap.fromTo(
            targets,
            { y: 25, autoAlpha: 0, force3D: true },
            {
              y: 0,
              autoAlpha: 1,
              duration: 0.7,
              stagger: 0.07,
              ease: 'power2.out',
              force3D: true,
              scrollTrigger: {
                trigger: faq,
                start: 'top 80%',
                toggleActions: 'play none none none',
                once: true,
              },
              onComplete: function() {
                gsap.set(targets, { clearProps: 'transform' });
              },
            }
          );
        }
      }
    );
  }

  let motionAttempts = 0;
  let motionInitDone = false;

  function ensureEditorialMotion() {
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;

    if (gsap && ScrollTrigger && typeof gsap.registerPlugin === 'function') {
      try {
        gsap.registerPlugin(ScrollTrigger);
      } catch (e) {
        // Plugin already registered
      }
      initEditorialMotion();
      motionInitDone = true;
      if (typeof ScrollTrigger.refresh === 'function') {
        ScrollTrigger.refresh();
      }
      return;
    }

    if (motionAttempts < 50) {
      motionAttempts++;
      setTimeout(ensureEditorialMotion, 50);
    }
  }

  function initEditorialMotion() {
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;

    if (gsap && ScrollTrigger && typeof gsap.registerPlugin === 'function') {
      try {
        gsap.registerPlugin(ScrollTrigger);
      } catch (e) {
        // Plugin already registered or handled
      }
    }

    initPinnedShowcase(gsap, ScrollTrigger);
    initCapsuleHover(gsap);
    initCapsuleNavigation(gsap, ScrollTrigger);
    initSectionEntrances(gsap, ScrollTrigger);
  }

  /* === Master Initialization Controller === */
  function initAll(container) {
    try {
      const scope = (container && container.querySelectorAll) ? container : document;

      initNavigation();
      initCarousels(scope);
      initTabs(scope);
      initAccordions(scope);
      initSwatches(scope);
      initFilters(scope);
      initCapsuleNavigation();
      ensureEditorialMotion();
      const cartManager = typeof initCartDrawer === 'function' ? initCartDrawer(scope) : null;
      if (typeof initQuickView === 'function' && cartManager) {
        initQuickView(cartManager, scope);
      }

      // Newsletter validation feedback
      const newsletterForm = (scope.querySelector ? scope.querySelector('.newsletter-form') : null) || document.querySelector('.newsletter-form');
      if (newsletterForm && !newsletterForm.dataset.newsletterInit) {
        newsletterForm.dataset.newsletterInit = 'true';
        newsletterForm.addEventListener('submit', (e) => {
          e.preventDefault();
          const input = newsletterForm.querySelector('.newsletter-input');
          if (input && input.value) {
            if (cartManager && typeof cartManager.showToast === 'function') {
              cartManager.showToast('Thank you for subscribing to FITUEYES Sound Journal.');
            }
            input.value = '';
          }
        });
      }

      window.__EDITORIAL_READY__ = true;
    } catch (err) {
      console.warn('Editorial landing init notice:', err);
    }
  }

  // 1. Initial DOM Ready Trigger
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initAll();
      ensureEditorialMotion();
    });
  } else {
    initAll();
    ensureEditorialMotion();
  }

  // 2. Backup check on window load (handles fast cache where load fired or is pending)
  if (document.readyState === 'complete') {
    ensureEditorialMotion();
    if (window.ScrollTrigger && typeof window.ScrollTrigger.refresh === 'function') {
      window.ScrollTrigger.refresh();
    }
  } else {
    window.addEventListener('load', function() {
      ensureEditorialMotion();
      if (window.ScrollTrigger && typeof window.ScrollTrigger.refresh === 'function') {
        window.ScrollTrigger.refresh();
      }
    });
  }

  // 3. BFCache (Back-Forward Cache) restoration support (critical for Windows Edge/Chrome)
  window.addEventListener('pageshow', function(event) {
    ensureEditorialMotion();
    if (window.ScrollTrigger && typeof window.ScrollTrigger.refresh === 'function') {
      setTimeout(function() {
        try { window.ScrollTrigger.refresh(); } catch (e) {}
      }, 100);
    }
  });

  // 4. Web Fonts ready layout shift stabilizer
  if (document.fonts && typeof document.fonts.ready?.then === 'function') {
    document.fonts.ready.then(function() {
      if (window.ScrollTrigger && typeof window.ScrollTrigger.refresh === 'function') {
        window.ScrollTrigger.refresh();
      }
    });
  }

  // Shopify Theme Editor support
  document.addEventListener('shopify:section:load', function(event) {
    initAll(event.target);
    if (window.ScrollTrigger && typeof window.ScrollTrigger.refresh === 'function') {
      setTimeout(function() {
        try { window.ScrollTrigger.refresh(); } catch (e) {}
      }, 120);
    }
  });

  document.addEventListener('shopify:section:reorder', function() {
    if (window.ScrollTrigger && typeof window.ScrollTrigger.refresh === 'function') {
      setTimeout(function() {
        try { window.ScrollTrigger.refresh(); } catch (e) {}
      }, 120);
    }
  });

  document.addEventListener('shopify:section:unload', function() {
    if (window.ScrollTrigger && typeof window.ScrollTrigger.refresh === 'function') {
      setTimeout(function() {
        try { window.ScrollTrigger.refresh(); } catch (e) {}
      }, 120);
    }
  });

  // Shopify Theme Editor block selection support (auto-opens selected block)
  document.addEventListener('shopify:block:select', function(event) {
    const blockEl = event.target;
    if (blockEl && blockEl.classList.contains('accordion-item')) {
      const header = blockEl.querySelector('.accordion-header');
      if (header && !blockEl.classList.contains('is-open')) {
        header.click();
      }
    }
    if (blockEl && blockEl.getAttribute('role') === 'tab') {
      blockEl.click();
    }
  });

})();
